// Reporting Data Service
// Fetches data from Firebase and generates report data for all report types
// This is the data layer for the entire reporting system

import {
  collection,
  getDocs,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Appointment, appointmentService } from './appointmentService';
import { Client, clientDataService } from './clientDataService';
import { shopSettingsService } from './shopSettingsService';
import { getReportingConfig } from './reportingConfigService';
import { inventoryService } from './inventoryService';
import { giftCardService } from './giftCardService';
import { giftCardsSoldInRange, giftCardsRedeemedInRange } from '../shared/giftCard';
import { parseTimeToMinutes } from '../shared/availability';
import { expenseService, Expense, EXPENSE_CATEGORIES } from './expenseService';
import { operatingCostService, OperatingCost } from './operatingCostService';
import {
  DailySummaryData,
  WeeklySummaryData,
  PayrollReportData,
  BarberPayrollBreakdown,
  BarberDailyPerformance,
  BarberPerformanceData
} from '../types/reporting';
import {
  calculateBarberPay,
  calculateTotalRevenue,
  calculateAverageTicket,
  calculateNoShowRate,
  calculateGrowth,
  roundToTwo,
} from '../utils/reportCalculations';
import { savePayrollSnapshot } from './payrollHistoryService';
import { ymd, parseYmd } from '../shared/dateUtils';
import { commissionForBarber, mergeCommissionPay } from '../shared/growth';
import { barberDataService } from './barberDataService';
import { loadFreshaHistory, sumDaysRange } from './freshaHistoryService';
import { loadClientHistory, barberRetentionById, barberRebookingById, barberTenureWeeksById, shopClientMetrics, type ClientRecord } from './clientHistoryService';
import { computeChurnRadar, type ChurnRadar } from '../shared/churnRadar';
import { scopedQuery, ensureActiveShopId } from './tenantQuery';
import { computeGrowth, growthNudges, resolveStageFromTenure, type GrowthInputs } from '../shared/growth';

class ReportingDataService {
  // ============================================================================
  // HELPER: Check if appointment is completed (supports both old and new systems)
  // ============================================================================

  /**
   * Checks if an appointment is completed
   * Handles BOTH appointment type systems:
   * - OLD: status === 'completed'
   * - NEW: appointmentStatus === 'checked_out'
   */
  private isAppointmentCompleted(apt: any): boolean {
    // NEW SYSTEM: Check appointmentStatus field (with underscores)
    if (apt.appointmentStatus === 'checked_out') {
      return true;
    }

    // OLD SYSTEM: Check status field (with hyphens)
    if (apt.status === 'completed') {
      return true;
    }

    return false;
  }

  // ============================================================================
  // TRANSACTION LOG: Read payment data from backend transaction file
  // ============================================================================

  /**
   * Read transactions from backend log file
   * Fetches from /api/payments/transactions endpoint (to be created)
   */
  private async getTransactions(startDate: string, endDate: string): Promise<any[]> {
    try {
      // Fetch transactions from backend API
      const response = await fetch(`/api/payments/transactions?startDate=${startDate}&endDate=${endDate}`);

      if (!response.ok) {
        console.warn('⚠️ Failed to fetch transactions from backend, using appointment data only');
        return [];
      }

      const data = await response.json();
      console.log(`✅ Loaded ${data.transactions?.length || 0} transactions from backend`);
      return data.transactions || [];
    } catch (error) {
      console.warn('⚠️ Error fetching transactions:', error);
      return [];
    }
  }

  /**
   * Calculate payment breakdown from actual transactions
   */
  private calculatePaymentBreakdown(transactions: any[]): {
    cash: number;
    card: number;
    mobilePay: number;
    storeCredit: number;
  } {
    const breakdown = {
      cash: 0,
      card: 0,
      mobilePay: 0,
      storeCredit: 0,
    };

    transactions.forEach((txn) => {
      const amount = txn.amount || 0;
      const method = txn.paymentMethod;

      switch (method) {
        case 'cash':
          breakdown.cash += amount;
          break;
        case 'card':
          breakdown.card += amount;
          break;
        case 'mobile':
        case 'mobile_link':
          breakdown.mobilePay += amount;
          break;
        case 'gift_card':
        case 'store_credit':
          breakdown.storeCredit += amount;
          break;
        default:
          // Unknown payment method, add to card as default
          breakdown.card += amount;
      }
    });

    return {
      cash: roundToTwo(breakdown.cash),
      card: roundToTwo(breakdown.card),
      mobilePay: roundToTwo(breakdown.mobilePay),
      storeCredit: roundToTwo(breakdown.storeCredit),
    };
  }

  /**
   * Format payment method for display
   */
  private formatPaymentMethod(method: string): string {
    const labels: { [key: string]: string } = {
      'cash': 'Cash',
      'card': 'Credit Card',
      'mobile': 'Mobile Payment',
      'mobile_link': 'Payment Link',
      'gift_card': 'Gift Card',
      'store_credit': 'Store Credit',
    };
    return labels[method] || method;
  }

  // ============================================================================
  // DAILY SUMMARY REPORT
  // ============================================================================

  /**
   * Get daily summary report data for a specific date
   */
  async getDailySummary(date: string): Promise<DailySummaryData> {
    console.log(`📊 Generating daily summary for ${date}...`);

    // Get all appointments for the date
    const appointments = await this.getAppointmentsByDate(date, date);
    const completedAppointments = appointments.filter((apt) => this.isAppointmentCompleted(apt));

    // Calculate revenue - use real tip data from appointments
    const { serviceRevenue, emergencyFees } = calculateTotalRevenue(completedAppointments);

    // 💰 REAL TIPS: Sum tipAmount from appointments instead of estimates
    const tips = completedAppointments.reduce(
      (sum, apt) => sum + (apt.tipAmount || 0), 0
    );

    // 💰 REAL PRODUCT REVENUE: Sum from productsSold on appointments
    const productRevenue = completedAppointments.reduce((sum, apt) => {
      if (apt.productsSold && apt.productsSold.length > 0) {
        return sum + apt.productsSold.reduce((pSum, p) => pSum + (p.total || 0), 0);
      }
      return sum;
    }, 0);

    // 🎁 GIFT CARDS (2026-08-15): sold = revenue (own line); redeemed = drawdown, NOT new revenue.
    // Sold is read from the giftCards collection (covers all 3 apps). Redeemed is read from the txn
    // ledger with its {service,product,tip} breakdown, which we SUBTRACT from service/product/tips so
    // dollars booked at sale aren't double-counted on redemption day. Barber payroll is untouched.
    const gcDayStart = new Date(`${date}T00:00:00`);
    const gcDayEnd = new Date(`${date}T23:59:59.999`);
    const gcCards: any[] = await giftCardService.getAllGiftCards().catch(() => []);
    const gcTxns: any[] = await giftCardService.getTransactionsInRange(gcDayStart, gcDayEnd).catch(() => []);
    const giftCardsSold = giftCardsSoldInRange(gcCards, gcDayStart, gcDayEnd);
    const giftCardsRedeemed = giftCardsRedeemedInRange(gcTxns, gcDayStart, gcDayEnd);
    const gcRedeemedSvc = gcTxns.reduce((s, t: any) => s + (t.type === 'redemption' ? (t.redemptionBreakdown?.service || 0) : 0), 0);
    const gcRedeemedProd = gcTxns.reduce((s, t: any) => s + (t.type === 'redemption' ? (t.redemptionBreakdown?.product || 0) : 0), 0);
    const gcRedeemedTip = gcTxns.reduce((s, t: any) => s + (t.type === 'redemption' ? (t.redemptionBreakdown?.tip || 0) : 0), 0);
    const adjServiceRevenue = Math.max(0, serviceRevenue - gcRedeemedSvc);
    const adjProductRevenue = Math.max(0, productRevenue - gcRedeemedProd);
    const adjTips = Math.max(0, tips - gcRedeemedTip);

    const totalRevenue = adjServiceRevenue + adjProductRevenue + adjTips + emergencyFees + giftCardsSold;

    // Count appointments — product-only RETAIL sales aren't appointments; exclude from the counts
    // (their product revenue still sums above from completedAppointments). Jose 2026-09-03.
    const serviceAppointments = completedAppointments.filter((apt) => (apt as any).saleType !== 'product');
    const total = appointments.filter((apt) => (apt as any).saleType !== 'product').length;
    const completed = serviceAppointments.length;
    const cancelled = appointments.filter((apt) => apt.status === 'cancelled').length;
    const noShows = appointments.filter((apt) => apt.status === 'no-show').length;
    const pending = appointments.filter((apt) => apt.status === 'scheduled' || apt.status === 'confirmed').length;
    const walkIns = appointments.filter((apt) => apt.source === 'walk-in' && (apt as any).saleType !== 'product').length;

    // Calculate metrics
    const averageTicket = calculateAverageTicket(totalRevenue, completed);
    const revenuePerAppointment = completed > 0 ? totalRevenue / completed : 0;
    const walkInPercentage = total > 0 ? (walkIns / total) * 100 : 0;
    const noShowRate = calculateNoShowRate(noShows, total);

    // 💰 REAL PAYMENT BREAKDOWN: Aggregate from appointment.paymentMethod
    let payments = {
      cash: 0,
      card: 0,
      mobilePay: 0,
      storeCredit: 0,
    };

    // First, try to get payment data from appointments
    completedAppointments.forEach((apt) => {
      let amount = (apt.price || 0) + (apt.tipAmount || 0);
      // SPLIT TENDER (Jose 2026-10-30): peel the cash portion off before crediting the primary method.
      const splitCash = (apt as any).splitCashAmount || 0;
      if (splitCash > 0) { const c = Math.min(splitCash, amount); payments.cash += c; amount -= c; }
      switch (apt.paymentMethod) {
        case 'cash':
          payments.cash += amount;
          break;
        case 'card':
          payments.card += amount;
          break;
        case 'mobile':
          payments.mobilePay += amount;
          break;
        case 'store_credit':
        case 'gift_card':
          payments.storeCredit += amount;
          break;
        default:
          // If no payment method recorded, don't add to any bucket yet
          break;
      }
    });

    // Check if we have real payment data
    const hasRealPaymentData = Object.values(payments).some(v => v > 0);

    if (!hasRealPaymentData) {
      // Fallback: try backend transactions
      try {
        const transactions = await this.getTransactions(date, date);
        if (transactions.length > 0) {
          payments = this.calculatePaymentBreakdown(transactions);
          console.log(`✅ Using real payment data from backend: ${transactions.length} transactions`);
        } else {
          // Last resort: estimates
          console.warn('⚠️ No payment data found, using estimates');
          payments = {
            cash: roundToTwo(totalRevenue * 0.3),
            card: roundToTwo(totalRevenue * 0.65),
            mobilePay: roundToTwo(totalRevenue * 0.05),
            storeCredit: 0,
          };
        }
      } catch (error) {
        console.error('❌ Error fetching transaction data:', error);
        payments = {
          cash: roundToTwo(totalRevenue * 0.3),
          card: roundToTwo(totalRevenue * 0.65),
          mobilePay: roundToTwo(totalRevenue * 0.05),
          storeCredit: 0,
        };
      }
    } else {
      // Round the real payment data
      payments = {
        cash: roundToTwo(payments.cash),
        card: roundToTwo(payments.card),
        mobilePay: roundToTwo(payments.mobilePay),
        storeCredit: roundToTwo(payments.storeCredit),
      };
      console.log(`✅ Using real payment data from appointments`);
    }

    // Busiest hour (analyze appointment times)
    const busiestHour = this.findBusiestHour(completedAppointments);

    // Staff performance
    const staffPerformance = await this.calculateDailyStaffPerformance(completedAppointments);

    // Client activity — classified from the REAL client roster by FIRST-VISIT date, not appointments.clientId
    // (~2% linked) or the lifetime `totalVisits` seed. new = first-ever visit is THIS day; returning = seen
    // before. Anonymous walk-ins aren't classifiable → excluded. BEHAVIOR-PIN: plausible-but-wrong audit.
    const _roster = await loadClientHistory().catch(() => [] as ClientRecord[]);
    const _byName = new Map<string, ClientRecord>();
    for (const c of _roster) _byName.set((c.name || '').trim().toLowerCase().replace(/\s+/g, ' '), c);
    const _WALK = new Set(['', 'walk-in', 'walk in', 'walkin']);
    const _seen = new Set<string>();
    let newClients = 0, returningClients = 0, firstTimeReturns = 0;
    for (const a of completedAppointments) {
      const nm = ((a as any).clientName || '').trim().toLowerCase().replace(/\s+/g, ' ');
      if (_WALK.has(nm) || _seen.has(nm)) continue;
      _seen.add(nm);
      const rec = _byName.get(nm);
      if (!rec || !rec.firstVisit) { newClients++; continue; }   // unknown name → treat as new this day
      if (rec.firstVisit >= date) newClients++;                   // first-ever visit is today → NEW
      else { returningClients++; if ((rec.totalVisits || 0) === 2) firstTimeReturns++; }
    }
    const vipClients = 0; // VIP tier isn't a real signal yet (membershipType is a seed) — don't fabricate a count.

    return {
      date,
      revenue: {
        total: roundToTwo(totalRevenue),
        service: roundToTwo(adjServiceRevenue),
        products: roundToTwo(adjProductRevenue),
        emergencyFees: roundToTwo(emergencyFees),
        tips: roundToTwo(adjTips),
        giftCardsSold: roundToTwo(giftCardsSold),
        giftCardsRedeemed: roundToTwo(giftCardsRedeemed),
      },
      appointments: {
        total,
        completed,
        pending,
        cancelled,
        noShows,
        walkIns,
      },
      metrics: {
        averageTicket: roundToTwo(averageTicket),
        revenuePerAppointment: roundToTwo(revenuePerAppointment),
        walkInPercentage: roundToTwo(walkInPercentage),
        noShowRate: roundToTwo(noShowRate),
      },
      payments,
      busiestHour,
      staffPerformance,
      clientActivity: {
        newClients,
        returningClients,
        firstTimeReturns,
        vipClients,
      },
    };
  }

  // ============================================================================
  // WEEKLY SUMMARY REPORT
  // ============================================================================

  /**
   * Get weekly summary report data
   */
  async getWeeklySummary(startDate: string, endDate: string): Promise<WeeklySummaryData> {
    console.log(`📊 Generating weekly summary for ${startDate} to ${endDate}...`);

    // Get appointments for the week
    const appointments = await this.getAppointmentsByDate(startDate, endDate);
    const completedAppointments = appointments.filter((apt) => this.isAppointmentCompleted(apt));

    // Calculate total metrics - REAL DATA from appointments
    const { serviceRevenue, emergencyFees } = calculateTotalRevenue(completedAppointments);

    // 💰 REAL TIPS: Sum tipAmount from appointments
    const tips = completedAppointments.reduce(
      (sum, apt) => sum + (apt.tipAmount || 0), 0
    );

    // 💰 REAL PRODUCT REVENUE: Sum from productsSold
    const productRevenue = completedAppointments.reduce((sum, apt) => {
      if (apt.productsSold && apt.productsSold.length > 0) {
        return sum + apt.productsSold.reduce((pSum, p) => pSum + (p.total || 0), 0);
      }
      return sum;
    }, 0);

    const totalRevenue = serviceRevenue + productRevenue + tips + emergencyFees;
    const totalAppointments = completedAppointments.filter((apt) => (apt as any).saleType !== 'product').length;
    const averageTicket = calculateAverageTicket(totalRevenue, totalAppointments);

    // Day-by-day breakdown
    const dailyBreakdown = await this.getDailyBreakdown(startDate, endDate, completedAppointments);

    // Calculate comparison with last week
    const lastWeekRange = this.getPreviousWeekRange(startDate);
    const lastWeekAppointments = await this.getAppointmentsByDate(
      lastWeekRange.startDate,
      lastWeekRange.endDate
    );
    const lastWeekCompleted = lastWeekAppointments.filter((apt) => this.isAppointmentCompleted(apt));
    const lastWeekRevenue = calculateTotalRevenue(lastWeekCompleted).serviceRevenue;
    const growthPercentage = calculateGrowth(totalRevenue, lastWeekRevenue);

    // Top performers
    const topPerformers = await this.getTopPerformers(completedAppointments);

    return {
      startDate,
      endDate,
      totalRevenue: roundToTwo(totalRevenue),
      totalAppointments,
      averageTicket: roundToTwo(averageTicket),
      dailyBreakdown,
      comparison: {
        lastWeekRevenue: roundToTwo(lastWeekRevenue),
        lastWeekAppointments: lastWeekCompleted.length,
        growthPercentage: roundToTwo(growthPercentage),
      },
      topPerformers,
    };
  }

  // ============================================================================
  // PAYROLL REPORT
  // ============================================================================

  /**
   * Get payroll report data for a period
   */
  async getPayrollReport(startDate: string, endDate: string): Promise<PayrollReportData> {
    console.log(`💰 Generating payroll report for ${startDate} to ${endDate}...`);

    const settings = await shopSettingsService.getSettings();
    // Commission is driven by Settings → Commission & Pay (the sliding-scale cliff) — the single
    // source of truth. (Jose 2026-09-01: payroll was ignoring it and using flat per-barber rates.)
    const commissionPay = mergeCommissionPay(await shopSettingsService.getSettingsByType('commissionPay'));
    // MONEY-CRITICAL (Jose 2026-10-28): load the private pay vault (flat-vs-sliding + rate) BEFORE computing
    // so a FLAT-50 barber is never read as mode-less → sliding (underpay). Was timing-dependent on an async
    // onSnapshot; a cold payroll run could compute before it fired. This awaits an authoritative read.
    const payVaultReadable = await barberDataService.ensurePrivatePayLoaded();
    if (!payVaultReadable) {
      // MONEY GUARD (Jose 2026-11-04): the pay vault (flat-vs-sliding + rate) could NOT be read — the operator
      // isn't elevated (owner/manager). Computing now would read every FLAT barber as mode-less → sliding →
      // UNDERPAY. REFUSE the run with a clear re-tap message instead of silently paying a wrong number.
      const err: any = new Error('Pay data is locked. Tap in as an owner or manager (PIN) to run payroll.');
      err.code = 'PAYROLL_LOCKED';
      throw err;
    }
    // ── PAYROLL SOURCE = reconciled freshaHistory (Jose 2026-09-09) ──────────────────────────
    // Barbers are paid on the SERVICE line (svcNet), which reconciles to the Financial-Summary /
    // Fresha "Sales Summary → Services" to the penny. Tips are EXACT for every barber (was per-
    // appointment tipAmount, which only some appointments carried → tips showed for one barber only).
    // Products are separate (retail spiff). Falls back to live appointments for post-cutover windows
    // that have no Fresha data. Commission still runs through the shop's sliding-scale engine.
    const barbers: BarberPayrollBreakdown[] = [];
    let totalServiceRevenue = 0;
    let totalProductRevenue = 0;
    let totalCommissionsPaid = 0;
    let totalTips = 0;

    const pushBarber = (barberId: string, barberName: string, serviceRevenue: number, productSales: number, tips: number, apptCount: number) => {
      const barberRec = barberDataService.getBarberById(barberId);
      const svcComm = commissionForBarber(barberRec, serviceRevenue, commissionPay.tiers);
      const serviceCommission = svcComm.commission;
      const commissionRate = roundToTwo(svcComm.rate * 100); // decimal rate → percent for display
      const productCommission = roundToTwo(productSales * (commissionPay.retailSpiffPct / 100));
      const totalCommission = roundToTwo(serviceCommission + productCommission);
      const totalPay = calculateBarberPay({ serviceCommission, productCommission, tips, bonuses: 0, emergencyFeeSplit: 0 });
      totalServiceRevenue += serviceRevenue;
      totalProductRevenue += productSales;
      totalCommissionsPaid += totalCommission;
      totalTips += tips;
      barbers.push({
        barberId, barberName,
        serviceRevenue: roundToTwo(serviceRevenue),
        productSales: roundToTwo(productSales),
        commissionRate,
        serviceCommission: roundToTwo(serviceCommission),
        productCommission: roundToTwo(productCommission),
        totalCommission: roundToTwo(totalCommission),
        tips: roundToTwo(tips),
        bonuses: 0,
        emergencyFeeSplit: 0,
        totalPay: roundToTwo(totalPay),
        appointments: apptCount,
        averageTicket: roundToTwo(calculateAverageTicket(serviceRevenue, apptCount)),
      });
    };

    // 1) freshaHistory path — reconciled per-barber-per-day figures for the window; track COVERED dates.
    const freshaBarbers = await loadFreshaHistory();
    const agg: Record<string, { name: string; svc: number; prod: number; tips: number; tickets: number; covered: Set<string> }> = {};
    for (const fb of freshaBarbers) {
      if (!fb.barberId) continue;
      const s = sumDaysRange(fb, startDate, endDate);
      const covered = new Set<string>();
      for (const iso of Object.keys(fb.days || {})) { if (iso >= startDate && iso <= endDate) covered.add(iso); }
      if (s.svcNet === 0 && s.prodNet === 0 && s.tips === 0 && s.tickets === 0 && covered.size === 0) continue;
      const rec = barberDataService.getBarberById(fb.barberId);
      const name = (rec ? `${rec.firstName || ''} ${rec.lastName || ''}`.trim() : '') || fb.barberName;
      agg[fb.barberId] = { name, svc: s.svcNet, prod: s.prodNet, tips: s.tips, tickets: s.tickets, covered };
    }

    // 2) BLEND (Jose 2026-09-10): add live completed-appointment days that Fresha does NOT already cover for
    //    that barber — so a pay week spanning the cutover (Fresha through day X + live SLIK sales after) pays
    //    BOTH. Prefer Fresha for its covered dates to avoid double-counting. (Was all-or-nothing → silently
    //    dropped every barber's post-cutover live earnings whenever the window also held ANY reconciled day.)
    const appointments = await this.getAppointmentsByDate(startDate, endDate);
    const completedAppointments = appointments.filter((apt) => this.isAppointmentCompleted(apt));
    for (const apt of completedAppointments) {
      const bid = apt.barberId; if (!bid) continue;
      if (agg[bid]?.covered.has(apt.date)) continue; // Fresha already counts this day for this barber
      const a = (agg[bid] ||= { name: apt.barberName, svc: 0, prod: 0, tips: 0, tickets: 0, covered: new Set<string>() });
      a.svc += apt.price || 0;
      a.prod += apt.productsSold?.reduce((p: number, x: any) => p + (x.total || 0), 0) || 0;
      a.tips += apt.tipAmount || 0;
      a.tickets += 1;
    }

    for (const [bid, a] of Object.entries(agg)) {
      if (a.svc === 0 && a.prod === 0 && a.tips === 0 && a.tickets === 0) continue;
      pushBarber(bid, a.name, a.svc, a.prod, a.tips, a.tickets);
    }

    // Sort by total pay (highest first)
    barbers.sort((a, b) => b.totalPay - a.totalPay);

    // Calculate shop totals
    const grossRevenue = totalServiceRevenue + totalProductRevenue;
    const shopProfit = grossRevenue - totalCommissionsPaid;

    const result: PayrollReportData = {
      period: {
        startDate,
        endDate,
        frequency: settings.reporting?.payroll?.frequency || 'weekly',
      },
      barbers,
      shopTotals: {
        grossRevenue: roundToTwo(grossRevenue),
        serviceRevenue: roundToTwo(totalServiceRevenue),
        productRevenue: roundToTwo(totalProductRevenue),
        totalCommissionsPaid: roundToTwo(totalCommissionsPaid),
        totalTips: roundToTwo(totalTips),
        shopProfit: roundToTwo(shopProfit),
      },
      generatedAt: new Date(),
    };

    // Persist payroll snapshot only for windows with real money — don't save $0 empty/future weeks as
    // "history" (a zeroed 2026-09-06 snapshot already exists). Audit fix (2026-09-10).
    if (barbers.length > 0 && grossRevenue > 0) savePayrollSnapshot(startDate, endDate, result).catch(() => {});

    return result;
  }

  // ============================================================================
  // BARBER PERFORMANCE REPORT
  // ============================================================================

  /**
   * Get barber performance data for a specific barber
   */
  async getBarberPerformance(
    barberId: string,
    startDate: string,
    endDate: string
  ): Promise<BarberPerformanceData> {
    console.log(`👤 Generating performance report for barber ${barberId}...`);

    const appointments = await this.getAppointmentsByDate(startDate, endDate);
    const barberAppointments = appointments.filter((apt) => apt.barberId === barberId);
    const completedAppointments = barberAppointments.filter((apt) => this.isAppointmentCompleted(apt));

    if (barberAppointments.length === 0) {
      throw new Error(`No appointments found for barber ${barberId} in the specified period`);
    }

    const barberName = barberAppointments[0].barberName;

    // Calculate revenue - REAL DATA from appointments
    const serviceRevenue = completedAppointments.reduce((sum, apt) => sum + (apt.price || 0), 0);

    // 💰 REAL PRODUCT REVENUE: Sum from productsSold
    const productRevenue = completedAppointments.reduce((sum, apt) => {
      if (apt.productsSold && apt.productsSold.length > 0) {
        return sum + apt.productsSold.reduce((pSum, p) => pSum + (p.total || 0), 0);
      }
      return sum;
    }, 0);

    // 💰 REAL TIPS: Sum tipAmount from appointments
    const tips = completedAppointments.reduce((sum, apt) => sum + (apt.tipAmount || 0), 0);
    const totalRevenue = serviceRevenue + productRevenue + tips;

    // Count appointment types
    const totalAppointments = completedAppointments.filter((apt) => (apt as any).saleType !== 'product').length;
    const { newClients, returningClients } = await this.classifyWindowClients(completedAppointments, startDate);
    const noShows = barberAppointments.filter((apt) => apt.status === 'no-show').length;

    // Calculate metrics
    const averageTicket = calculateAverageTicket(totalRevenue, totalAppointments);
    const retentionRate = totalAppointments > 0 ? (returningClients / totalAppointments) * 100 : 0;
    const productUpsellRate = 0; // TODO: Calculate when product tracking is implemented
    const onTimeRate = 100; // TODO: Calculate when on-time tracking is implemented
    const noShowRate = calculateNoShowRate(noShows, barberAppointments.length);

    // Service breakdown
    const serviceBreakdown = this.calculateServiceBreakdown(completedAppointments);

    // Calculate comparison with previous period
    const previousPeriodRange = this.getPreviousPeriodRange(startDate, endDate);
    const previousAppointments = await this.getAppointmentsByDate(
      previousPeriodRange.startDate,
      previousPeriodRange.endDate
    );
    const previousBarberAppointments = previousAppointments.filter(
      (apt) => apt.barberId === barberId && this.isAppointmentCompleted(apt)
    );
    const previousRevenue = previousBarberAppointments.reduce((sum, apt) => sum + (apt.price || 0), 0);
    const growthPercentage = calculateGrowth(totalRevenue, previousRevenue);

    return {
      barberId,
      barberName,
      period: {
        startDate,
        endDate,
      },
      revenue: {
        total: roundToTwo(totalRevenue),
        service: roundToTwo(serviceRevenue),
        products: roundToTwo(productRevenue),
        tips: roundToTwo(tips),
      },
      appointments: {
        total: totalAppointments,
        newClients,
        returningClients,
        noShows,
      },
      metrics: {
        averageTicket: roundToTwo(averageTicket),
        retentionRate: roundToTwo(retentionRate),
        productUpsellRate: roundToTwo(productUpsellRate),
        onTimeRate: roundToTwo(onTimeRate),
        noShowRate: roundToTwo(noShowRate),
      },
      serviceBreakdown,
      comparison: {
        previousPeriodRevenue: roundToTwo(previousRevenue),
        growthPercentage: roundToTwo(growthPercentage),
      },
    };
  }

  // ============================================================================
  // MONTHLY REPORT
  // ============================================================================

  /**
   * Get monthly summary report data
   */
  async getMonthlyReport(year: number, month: number): Promise<any> {
    console.log(`📊 Generating monthly report for ${month}/${year}...`);

    // Calculate date range for the month
    const startDate = ymd(new Date(year, month - 1, 1));
    const endDate = ymd(new Date(year, month, 0));

    // Get appointments for the month
    const appointments = await this.getAppointmentsByDate(startDate, endDate);
    const completedAppointments = appointments.filter((apt) => this.isAppointmentCompleted(apt));

    // Calculate revenue - REAL DATA from appointments
    const { serviceRevenue, emergencyFees } = calculateTotalRevenue(completedAppointments);

    // 💰 REAL TIPS: Sum tipAmount from appointments
    const tips = completedAppointments.reduce(
      (sum, apt) => sum + (apt.tipAmount || 0), 0
    );

    // 💰 REAL PRODUCT REVENUE: Sum from productsSold
    const productRevenue = completedAppointments.reduce((sum, apt) => {
      if (apt.productsSold && apt.productsSold.length > 0) {
        return sum + apt.productsSold.reduce((pSum, p) => pSum + (p.total || 0), 0);
      }
      return sum;
    }, 0);

    const totalRevenue = serviceRevenue + productRevenue + tips + emergencyFees;

    // Calculate metrics
    const totalAppointments = completedAppointments.filter((apt) => (apt as any).saleType !== 'product').length;
    const averageTicket = calculateAverageTicket(totalRevenue, totalAppointments);
    const noShows = appointments.filter((apt) => apt.status === 'no-show').length;
    const noShowRate = calculateNoShowRate(noShows, appointments.length);

    // Get client activity — REAL new/returning from the roster by first-visit (not appointments.clientId).
    const { newClients, returningClients, distinct: uniqueClients } = await this.classifyWindowClients(completedAppointments, startDate);

    // Weekly breakdown
    const weeklyBreakdown = await this.getWeeklyBreakdownForMonth(startDate, endDate, completedAppointments);

    // Staff performance
    const staffPerformance = await this.calculateDailyStaffPerformance(completedAppointments);

    // Service breakdown
    const serviceBreakdown = this.calculateServiceBreakdown(completedAppointments);

    // Calculate comparison with previous month
    const previousMonthRange = this.getPreviousMonthRange(year, month);
    const previousMonthAppointments = await this.getAppointmentsByDate(
      previousMonthRange.startDate,
      previousMonthRange.endDate
    );
    const previousMonthCompleted = previousMonthAppointments.filter((apt) => this.isAppointmentCompleted(apt));
    const previousMonthRevenue = calculateTotalRevenue(previousMonthCompleted).serviceRevenue;
    const growthPercentage = calculateGrowth(totalRevenue, previousMonthRevenue);

    // Get busiest day
    const dailyData = await this.getDailyBreakdown(startDate, endDate, completedAppointments);
    const busiestDay = dailyData.reduce((max, day) =>
      day.revenue > max.revenue ? day : max,
      { date: '', revenue: 0, appointments: 0 }
    );

    return {
      month,
      year,
      period: {
        startDate,
        endDate,
      },
      revenue: {
        total: roundToTwo(totalRevenue),
        service: roundToTwo(serviceRevenue),
        products: roundToTwo(productRevenue),
        tips: roundToTwo(tips),
        emergencyFees: roundToTwo(emergencyFees),
      },
      appointments: {
        total: totalAppointments,
        scheduled: appointments.length,
        noShows,
        cancelled: appointments.filter((apt) => apt.status === 'cancelled').length,
      },
      metrics: {
        averageTicket: roundToTwo(averageTicket),
        noShowRate: roundToTwo(noShowRate),
        newClientRate: totalAppointments > 0 ? roundToTwo((newClients / totalAppointments) * 100) : 0,
      },
      clientActivity: {
        newClients,
        returningClients,
        totalUniqueClients: uniqueClients,
      },
      weeklyBreakdown,
      staffPerformance,
      serviceBreakdown,
      busiestDay,
      comparison: {
        previousMonthRevenue: roundToTwo(previousMonthRevenue),
        growthPercentage: roundToTwo(growthPercentage),
        previousMonthAppointments: previousMonthCompleted.length,
      },
    };
  }

  // ============================================================================
  // FINANCE SUMMARY REPORT (Jose 2026-08-31)
  // Accountant-grade full breakdown modeled on Fresha's Finance Summary, in SLIK-native
  // (cash-basis) terms: Sales → subtotals → Payments-by-method → Redemptions, with month
  // columns. Every line item named so there's zero ambiguity when emailed to the accountant.
  // Reuses the exact revenue/discount/refund/gift-card definitions the other reports use;
  // TAX is the ACTUAL charged product tax (Σ appointment.tax), NOT the old net×8.25% estimate.
  // ============================================================================

  /** One period's finance line items (single month or arbitrary range). */
  private async computeFinancePeriod(startDate: string, endDate: string): Promise<any> {
    const appointments = await this.getAppointmentsByDate(startDate, endDate);
    const completed = appointments.filter((apt) => this.isAppointmentCompleted(apt));

    const { serviceRevenue, emergencyFees } = calculateTotalRevenue(completed);
    const tips = completed.reduce((s, a) => s + (a.tipAmount || 0), 0);
    const productRevenue = completed.reduce(
      (s, a) => s + ((a.productsSold || []).reduce((p, x) => p + (x.total || 0), 0)),
      0
    );

    // Gift cards: sold = revenue (own line); redeemed = drawdown (subtracted so sale-day $ isn't double-counted).
    const gcStart = new Date(`${startDate}T00:00:00`);
    const gcEnd = new Date(`${endDate}T23:59:59.999`);
    const gcCards: any[] = await giftCardService.getAllGiftCards().catch(() => []);
    const gcTxns: any[] = await giftCardService.getTransactionsInRange(gcStart, gcEnd).catch(() => []);
    const giftCardsSold = giftCardsSoldInRange(gcCards, gcStart, gcEnd);
    const giftCardsRedeemed = giftCardsRedeemedInRange(gcTxns, gcStart, gcEnd);
    const gcRedSvc = gcTxns.reduce((s, t: any) => s + (t.type === 'redemption' ? (t.redemptionBreakdown?.service || 0) : 0), 0);
    const gcRedProd = gcTxns.reduce((s, t: any) => s + (t.type === 'redemption' ? (t.redemptionBreakdown?.product || 0) : 0), 0);
    const gcRedTip = gcTxns.reduce((s, t: any) => s + (t.type === 'redemption' ? (t.redemptionBreakdown?.tip || 0) : 0), 0);
    const adjService = Math.max(0, serviceRevenue - gcRedSvc);
    const adjProduct = Math.max(0, productRevenue - gcRedProd);
    const adjTips = Math.max(0, tips - gcRedTip);

    // Sales math — same definitions used by the Product/Service reports.
    const grossSales = adjService + adjProduct;
    const discounts = completed.reduce((s, a) => s + (a.discountAmount || 0), 0);
    const refunds = completed.reduce((s, a) => s + (a.refundAmount || 0), 0);
    const netSales = grossSales - discounts - refunds;
    // ACTUAL tax charged (product-only 9% stored per sale at checkout) — not the old estimate.
    const tax = completed.reduce((s, a) => s + ((a as any).tax || 0), 0);

    // Payments grouped by method (what actually hit the till = price + tip + tax).
    const pay = { cash: 0, card: 0, digitalWallet: 0, slikCoins: 0, giftCard: 0, other: 0 };
    completed.forEach((a) => {
      let amt = (a.price || 0) + (a.tipAmount || 0) + ((a as any).tax || 0);
      // SPLIT TENDER (Jose 2026-10-30): a sale paid part-cash/part-card stores the cash portion in
      // `splitCashAmount`; peel it to cash first, then the switch credits the remainder to the card/primary
      // method. Money is conserved (peeled from the same amt), just categorized correctly.
      const splitCash = (a as any).splitCashAmount || 0;
      if (splitCash > 0) { const c = Math.min(splitCash, amt); pay.cash += c; amt -= c; }
      switch (a.paymentMethod as string) {
        case 'cash': pay.cash += amt; break;
        case 'card': pay.card += amt; break;
        case 'mobile':
        case 'digital_wallet': pay.digitalWallet += amt; break;
        case 'digital_coins':
        case 'slik-coins': pay.slikCoins += amt; break;
        case 'gift_card':
        case 'store_credit': pay.giftCard += amt; break;
        default: pay.other += amt; break;
      }
    });
    const totalPayments = pay.cash + pay.card + pay.digitalWallet + pay.slikCoins + pay.giftCard + pay.other;

    const r2 = roundToTwo;
    return {
      service: r2(adjService),
      product: r2(adjProduct),
      grossSales: r2(grossSales),
      discounts: r2(discounts),
      refunds: r2(refunds),
      netSales: r2(netSales),
      tax: r2(tax),
      tips: r2(adjTips),
      giftCardSales: r2(giftCardsSold),
      emergencyFees: r2(emergencyFees),
      totalCollected: r2(netSales + tax + adjTips + giftCardsSold + emergencyFees),
      payCash: r2(pay.cash),
      payCard: r2(pay.card),
      payDigitalWallet: r2(pay.digitalWallet),
      paySlikCoins: r2(pay.slikCoins),
      payGiftCard: r2(pay.giftCard),
      payOther: r2(pay.other),
      totalPayments: r2(totalPayments),
      giftCardRedemptions: r2(giftCardsRedeemed),
      totalRedemptions: r2(giftCardsRedeemed),
      appointments: completed.length,
    };
  }

  /**
   * Finance Summary — a `monthsBack`-month grid (newest first) plus a Total column.
   * Each cell is a full finance breakdown for that month; the report/export lays these out
   * as named line items × period columns.
   */
  private static FINANCE_KEYS = [
    'service', 'product', 'grossSales', 'discounts', 'refunds', 'netSales', 'tax', 'tips',
    'giftCardSales', 'emergencyFees', 'totalCollected', 'payCash', 'payCard', 'payDigitalWallet',
    'paySlikCoins', 'payGiftCard', 'payOther', 'totalPayments', 'giftCardRedemptions',
    'totalRedemptions', 'appointments',
  ];

  /** Compute finance columns for a list of month-first Dates (already newest-first) + a Total column. */
  private async buildFinanceSummary(monthFirsts: Date[]): Promise<any> {
    const periods: any[] = [];
    for (const d of monthFirsts) {
      const start = ymd(new Date(d.getFullYear(), d.getMonth(), 1));
      const end = ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
      const data = await this.computeFinancePeriod(start, end);
      periods.push({
        label: d.toLocaleString('en-US', { month: 'short', year: 'numeric' }),
        start,
        end,
        data,
      });
    }
    const total: any = {};
    ReportingDataService.FINANCE_KEYS.forEach((k) => {
      total[k] = roundToTwo(periods.reduce((s, p) => s + (p.data[k] || 0), 0));
    });
    return { periods, total };
  }

  /** Quick preset: the last `monthsBack` months (newest first) + Total. */
  async getFinanceSummary(monthsBack: number = 6): Promise<any> {
    console.log(`📊 Generating finance summary (last ${monthsBack} months)...`);
    const now = new Date();
    const monthFirsts: Date[] = [];
    for (let i = 0; i < monthsBack; i++) monthFirsts.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    return { ...(await this.buildFinanceSummary(monthFirsts)), monthsBack };
  }

  /**
   * Custom range: every month from `startMonth`..`endMonth` inclusive (each 'YYYY-MM'), newest first,
   * + a Total column. Pass the same month for both to spot-check a SINGLE month. Capped at 36 months.
   */
  async getFinanceSummaryRange(startMonth: string, endMonth: string): Promise<any> {
    console.log(`📊 Generating finance summary ${startMonth} → ${endMonth}...`);
    const [sy, sm] = startMonth.split('-').map(Number);
    const [ey, em] = endMonth.split('-').map(Number);
    let s = new Date(sy, (sm || 1) - 1, 1);
    let e = new Date(ey, (em || 1) - 1, 1);
    if (e < s) { const t = s; s = e; e = t; }
    const monthFirsts: Date[] = [];
    let cur = new Date(e.getFullYear(), e.getMonth(), 1); // newest first
    let guard = 0;
    while (cur >= s && guard < 36) {
      monthFirsts.push(new Date(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth() - 1, 1);
      guard++;
    }
    return { ...(await this.buildFinanceSummary(monthFirsts)), custom: { startMonth, endMonth } };
  }

  /**
   * Custom DAY range: a single column for exactly `startDate`..`endDate` ('YYYY-MM-DD'), e.g. one week.
   * Uses the same aggregation as the month columns, so numbers reconcile. Total = the span itself.
   */
  async getFinanceSummaryForDates(startDate: string, endDate: string): Promise<any> {
    let s = startDate, e = endDate;
    if (e < s) { const t = s; s = e; e = t; }
    console.log(`📊 Generating finance summary (days) ${s} → ${e}...`);
    const data = await this.computeFinancePeriod(s, e);
    const fmt = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const label = s === e ? fmt(s) : `${fmt(s)} – ${fmt(e)}`;
    const total: any = {};
    ReportingDataService.FINANCE_KEYS.forEach((k) => { total[k] = data[k]; });
    return { periods: [{ label, start: s, end: e, data }], total, dayRange: { startDate: s, endDate: e } };
  }

  // ============================================================================
  // PROFIT & LOSS REPORT (Jose 2026-09-04) — the profitability lens on top of Finance Summary.
  // Revenue (service + product + gift cards + emergency, net of discounts/refunds) − COGS − operating
  // expenses (from the Expense tab, by category) = NET PROFIT. Reuses computeFinancePeriod (revenue,
  // gift-card-adjusted so nothing double-counts) + getInventoryReport (COGS) + expenseService (expenses).
  // Tips & tax are pass-through (barber / state) — carried as memo lines, EXCLUDED from profit.
  // Same { periods, total } grid shape as Finance Summary so one table/CSV/PDF renders it; plus a
  // dynamic `expenseCategories` list (union across periods) for the expense rows.
  // ============================================================================
  private async computePLPeriod(startDate: string, endDate: string, allExpenses: Expense[], opMonthly = 0): Promise<any> {
    const fin = await this.computeFinancePeriod(startDate, endDate);
    let cogs = 0;
    try {
      const inv = await this.getInventoryReport(startDate, endDate);
      cogs = inv?.summary?.cogs || 0;
    } catch (e) {
      console.warn('⚠️ P&L: COGS lookup failed, treating as 0:', e);
    }
    // Expenses whose incurred-date falls in the span, summed by category. 'Product Inventory' is
    // EXCLUDED from operating expenses — product cost is represented by COGS (above), so counting a
    // Product-Inventory expense here too would double-count against the same dollars.
    const byCat: Record<string, number> = {};
    allExpenses.forEach((x) => {
      if (x.category === 'Product Inventory') return;
      if (x.date >= startDate && x.date <= endDate) byCat[x.category] = (byCat[x.category] || 0) + (x.amount || 0);
    });
    // Recurring software/infrastructure cost (Jose 2026-10-09) — the services SLIK runs on (operatingCosts
    // list), added as its own expense line at the calendar-month fraction this span covers: exactly 1× for
    // a full-month column, pro-rated for a partial/day range. Revenue is untouched → no golden drift.
    if (opMonthly > 0) {
      const frac = ReportingDataService.monthFraction(startDate, endDate);
      if (frac > 0) byCat['Software & Infrastructure'] = (byCat['Software & Infrastructure'] || 0) + opMonthly * frac;
    }
    const totalExpenses = Object.values(byCat).reduce((s, v) => s + v, 0);

    const serviceRevenue = fin.service;
    const productRevenue = fin.product;
    const giftCardSales = fin.giftCardSales;
    const emergencyFees = fin.emergencyFees;
    const grossRevenue = serviceRevenue + productRevenue + giftCardSales + emergencyFees;
    const discounts = fin.discounts;
    const refunds = fin.refunds;
    const netRevenue = grossRevenue - discounts - refunds;
    const grossProfit = netRevenue - cogs;
    const netProfit = grossProfit - totalExpenses;

    const r2 = roundToTwo;
    const rounded: Record<string, number> = {};
    Object.keys(byCat).forEach((k) => { rounded[k] = r2(byCat[k]); });
    return {
      serviceRevenue: r2(serviceRevenue),
      productRevenue: r2(productRevenue),
      giftCardSales: r2(giftCardSales),
      emergencyFees: r2(emergencyFees),
      grossRevenue: r2(grossRevenue),
      discounts: r2(discounts),
      refunds: r2(refunds),
      netRevenue: r2(netRevenue),
      cogs: r2(cogs),
      grossProfit: r2(grossProfit),
      expenses: rounded,
      totalExpenses: r2(totalExpenses),
      netProfit: r2(netProfit),
      netMarginPct: netRevenue > 0 ? r2((netProfit / netRevenue) * 100) : 0,
      // Memo (excluded from profit) — pass-through amounts.
      tips: r2(fin.tips),
      tax: r2(fin.tax),
    };
  }

  private static PL_SCALAR_KEYS = [
    'serviceRevenue', 'productRevenue', 'giftCardSales', 'emergencyFees', 'grossRevenue',
    'discounts', 'refunds', 'netRevenue', 'cogs', 'grossProfit', 'totalExpenses', 'netProfit',
    'tips', 'tax',
  ];

  /** Calendar-month fraction a [start,end] inclusive (YYYY-MM-DD) span covers: 1.0 for a full month,
   *  ~0.23 for a 7-day week, 6.0 for six full months. Pro-rates a fixed MONTHLY cost across a P&L span
   *  so a full-month column gets exactly 1× and a partial/day range gets its true share. */
  private static monthFraction(startYmd: string, endYmd: string): number {
    const s = new Date(`${startYmd}T00:00:00`);
    const e = new Date(`${endYmd}T00:00:00`);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0;
    let total = 0;
    let cur = new Date(s.getFullYear(), s.getMonth(), 1);
    while (cur <= e) {
      const mStart = new Date(cur.getFullYear(), cur.getMonth(), 1);
      const mEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      const daysInMonth = mEnd.getDate();
      const oStart = s > mStart ? s : mStart;
      const oEnd = e < mEnd ? e : mEnd;
      const oDays = Math.floor((oEnd.getTime() - oStart.getTime()) / 86400000) + 1;
      if (oDays > 0) total += oDays / daysInMonth;
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    return total;
  }

  /** Order categories: known EXPENSE_CATEGORIES first (in their canonical order), then any free-text extras. */
  private static orderExpenseCategories(cats: string[]): string[] {
    const known = (EXPENSE_CATEGORIES as readonly string[]).filter((c) => cats.includes(c));
    const extras = cats.filter((c) => !(EXPENSE_CATEGORIES as readonly string[]).includes(c)).sort();
    return [...known, ...extras];
  }

  /** Build a P&L grid (periods newest-first + Total column) from a list of month-first Dates. */
  private async buildProfitAndLoss(monthFirsts: Date[]): Promise<any> {
    const allExpenses: Expense[] = await expenseService.listExpenses().catch(() => []);
    const opMonthly = (await operatingCostService.list().catch((): OperatingCost[] => [])).reduce((s, c) => s + (Number(c.monthlyCost) || 0), 0);
    const periods: any[] = [];
    for (const d of monthFirsts) {
      const start = ymd(new Date(d.getFullYear(), d.getMonth(), 1));
      const end = ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
      const data = await this.computePLPeriod(start, end, allExpenses, opMonthly);
      periods.push({
        label: d.toLocaleString('en-US', { month: 'short', year: 'numeric' }),
        start,
        end,
        data,
      });
    }
    const catSet = new Set<string>();
    periods.forEach((p) => Object.keys(p.data.expenses).forEach((c) => catSet.add(c)));
    const expenseCategories = ReportingDataService.orderExpenseCategories([...catSet]);

    const total: any = { expenses: {} };
    ReportingDataService.PL_SCALAR_KEYS.forEach((k) => {
      total[k] = roundToTwo(periods.reduce((s, p) => s + (p.data[k] || 0), 0));
    });
    expenseCategories.forEach((c) => {
      total.expenses[c] = roundToTwo(periods.reduce((s, p) => s + (p.data.expenses[c] || 0), 0));
    });
    total.netMarginPct = total.netRevenue > 0 ? roundToTwo((total.netProfit / total.netRevenue) * 100) : 0;
    return { periods, total, expenseCategories };
  }

  /** Quick preset: last `monthsBack` months (newest first) + Total. */
  async getProfitAndLoss(monthsBack: number = 6): Promise<any> {
    console.log(`📉 Generating P&L (last ${monthsBack} months)...`);
    const now = new Date();
    const monthFirsts: Date[] = [];
    for (let i = 0; i < monthsBack; i++) monthFirsts.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    return { ...(await this.buildProfitAndLoss(monthFirsts)), monthsBack };
  }

  /** Custom month range 'YYYY-MM'..'YYYY-MM' (newest first) + Total. Capped at 36 months. */
  async getProfitAndLossRange(startMonth: string, endMonth: string): Promise<any> {
    console.log(`📉 Generating P&L ${startMonth} → ${endMonth}...`);
    const [sy, sm] = startMonth.split('-').map(Number);
    const [ey, em] = endMonth.split('-').map(Number);
    let s = new Date(sy, (sm || 1) - 1, 1);
    let e = new Date(ey, (em || 1) - 1, 1);
    if (e < s) { const t = s; s = e; e = t; }
    const monthFirsts: Date[] = [];
    let cur = new Date(e.getFullYear(), e.getMonth(), 1);
    let guard = 0;
    while (cur >= s && guard < 36) {
      monthFirsts.push(new Date(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth() - 1, 1);
      guard++;
    }
    return { ...(await this.buildProfitAndLoss(monthFirsts)), custom: { startMonth, endMonth } };
  }

  /** Custom DAY span 'YYYY-MM-DD'..'YYYY-MM-DD' as a single column (e.g. a week). Total = the span. */
  async getProfitAndLossForDates(startDate: string, endDate: string): Promise<any> {
    let s = startDate, e = endDate;
    if (e < s) { const t = s; s = e; e = t; }
    console.log(`📉 Generating P&L (days) ${s} → ${e}...`);
    const allExpenses: Expense[] = await expenseService.listExpenses().catch(() => []);
    const opMonthly = (await operatingCostService.list().catch((): OperatingCost[] => [])).reduce((s2, c) => s2 + (Number(c.monthlyCost) || 0), 0);
    const data = await this.computePLPeriod(s, e, allExpenses, opMonthly);
    const fmt = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const label = s === e ? fmt(s) : `${fmt(s)} – ${fmt(e)}`;
    const expenseCategories = ReportingDataService.orderExpenseCategories(Object.keys(data.expenses));
    return { periods: [{ label, start: s, end: e, data }], total: data, expenseCategories, dayRange: { startDate: s, endDate: e } };
  }

  /**
   * PRODUCT RECONCILIATION (Jose 2026-09-04) — the confirming layer between the bank and the books.
   * Compares what the BANK paid to product vendors (imported statement lines in `inventory_purchases`)
   * against what INVENTORY says was received (restock units × unit cost), with COGS for context. A gap
   * means product was paid for but never logged as received (missing entry / shrinkage) — surfaced, not
   * buried. The P&L uses COGS only, so this NEVER double-counts; it only confirms.
   */
  async getProductReconciliation(startDate: string, endDate: string): Promise<any> {
    await inventoryService.ready();
    const config = await getReportingConfig();
    const cogsPct = config.defaultProductCogsPct ?? 0.5;
    const products = inventoryService.getAllProducts();
    const byId = new Map<string, any>();
    products.forEach((p) => byId.set(p.id, p));
    const costPerUnit = (p: any): number =>
      !p ? 0 : p.unitCost && p.unitCost > 0 ? p.unitCost : p.retailPrice && p.retailPrice > 0 ? p.retailPrice * cogsPct : 0;

    // What inventory says came in (received) in the window.
    let receivedValue = 0;
    let unitsReceived = 0;
    try {
      const restock = await inventoryService.getFilteredTransactions({
        type: 'restock',
        startDate: new Date(startDate),
        endDate: new Date(endDate + 'T23:59:59'),
      });
      restock.forEach((t: any) => {
        const units = Math.abs(t.quantityChange || t.quantity || 0);
        receivedValue += units * costPerUnit(byId.get(t.productId));
        unitsReceived += units;
      });
    } catch (e) {
      console.warn('⚠️ reconciliation: restock txns fetch failed:', e);
    }

    // What the bank paid to product vendors (imported statement lines).
    let bankProductSpend = 0;
    let purchaseCount = 0;
    try {
      const snap = await getDocs(collection(db, 'inventory_purchases'));
      snap.docs
        .map((d) => d.data() as any)
        .filter((x) => x.date >= startDate && x.date <= endDate)
        .forEach((x) => { bankProductSpend += Number(x.amount) || 0; purchaseCount++; });
    } catch (e) {
      console.warn('⚠️ reconciliation: inventory_purchases fetch failed:', e);
    }

    let cogs = 0;
    try { const rep = await this.getInventoryReport(startDate, endDate); cogs = rep?.summary?.cogs || 0; } catch {}

    const variance = roundToTwo(bankProductSpend - receivedValue);
    const tolerance = Math.max(1, bankProductSpend * 0.02);
    const matched = purchaseCount === 0 || Math.abs(variance) <= tolerance;
    return {
      period: { startDate, endDate },
      bankProductSpend: roundToTwo(bankProductSpend),
      receivedValue: roundToTwo(receivedValue),
      unitsReceived,
      cogs: roundToTwo(cogs),
      variance,
      matched,
      purchaseCount,
    };
  }

  // ============================================================================
  // INVENTORY REPORT (D1, 2026-09-01) — stock value + COGS + low stock + turnover.
  // READ-ONLY: reads inventoryService (cache-backed via ready()). Binds to real Product fields
  // (currentQuantity, unitCost, retailPrice, parLevel, stockStatus). COGS = Σ(units sold × unit cost)
  // from type:'sale' transactions in the period; when a product has no unitCost, falls back to
  // retailPrice × reportingConfig.defaultProductCogsPct (A1). Turnover ≈ COGS ÷ current stock value.
  // ============================================================================
  async getInventoryReport(startDate: string, endDate: string): Promise<any> {
    console.log(`📦 Generating inventory report ${startDate} → ${endDate}...`);
    await inventoryService.ready();
    const config = await getReportingConfig();
    const cogsPct = config.defaultProductCogsPct ?? 0.5;

    const products = inventoryService.getAllProducts();
    const stats = inventoryService.getStats();
    const byId = new Map<string, any>();
    products.forEach((p) => byId.set(p.id, p));

    // Sale transactions in range — same call shape getProductSalesReport uses.
    let saleTxns: any[] = [];
    try {
      saleTxns = await inventoryService.getFilteredTransactions({
        type: 'sale',
        startDate: new Date(startDate),
        endDate: new Date(endDate + 'T23:59:59'),
      });
    } catch (e) {
      console.warn('⚠️ inventory sale txns fetch failed:', e);
    }

    const costPerUnit = (p: any): number => {
      if (!p) return 0;
      if (p.unitCost && p.unitCost > 0) return p.unitCost;
      if (p.retailPrice && p.retailPrice > 0) return p.retailPrice * cogsPct;
      return 0;
    };

    let cogs = 0;
    let unitsSold = 0;
    const perProduct = new Map<string, { productName: string; units: number; cogs: number }>();
    saleTxns.forEach((t) => {
      const units = Math.abs(t.quantityChange || t.quantity || 0);
      if (!units) return;
      const p = byId.get(t.productId);
      const lineCogs = units * costPerUnit(p);
      cogs += lineCogs;
      unitsSold += units;
      const key = t.productId || t.productName || 'unknown';
      const cur = perProduct.get(key) || { productName: p?.name || t.productName || 'Unknown', units: 0, cogs: 0 };
      cur.units += units;
      cur.cogs += lineCogs;
      perProduct.set(key, cur);
    });

    const stockValue = roundToTwo(stats.totalInventoryValue || 0);
    const retailValue = roundToTwo(products.reduce((s, p) => s + (p.currentQuantity || 0) * (p.retailPrice || 0), 0));
    const turnover = stockValue > 0 ? roundToTwo(cogs / stockValue) : 0;
    const potentialMargin = roundToTwo(retailValue - stockValue);

    const lowStock = inventoryService.getLowStockProducts()
      .map((p: any) => ({
        name: p.name,
        category: p.category,
        currentQuantity: p.currentQuantity,
        parLevel: p.parLevel,
        unitCost: roundToTwo(p.unitCost || 0),
        status: p.stockStatus,
      }))
      .sort((a, b) => a.currentQuantity - b.currentQuantity);

    const topCogs = Array.from(perProduct.values())
      .map((r) => ({ ...r, cogs: roundToTwo(r.cogs) }))
      .sort((a, b) => b.cogs - a.cogs)
      .slice(0, 25);

    const byCategory = Object.entries(inventoryService.getInventoryValueByCategory())
      .map(([category, value]) => ({ category, value: roundToTwo(value as number) }))
      .filter((c) => c.value > 0)
      .sort((a, b) => b.value - a.value);

    return {
      period: { startDate, endDate },
      summary: {
        totalProducts: stats.totalProducts,
        activeProducts: stats.activeProducts,
        stockValue,
        retailValue,
        potentialMargin,
        cogs: roundToTwo(cogs),
        unitsSold,
        turnover,
        lowStockCount: (stats.lowStockCount || 0) + (stats.criticalStockCount || 0) + (stats.outOfStockCount || 0),
        outOfStockCount: stats.outOfStockCount || 0,
        cogsPct,
      },
      lowStock,
      topCogs,
      byCategory,
    };
  }

  // ============================================================================
  // PRODUCT SALES REPORT
  // ============================================================================

  /**
   * Get product sales report data
   * 💰 REAL DATA: Queries inventory_transactions and appointment.productsSold
   */
  async getProductSalesReport(startDate: string, endDate: string): Promise<any> {
    console.log(`📊 Generating product sales report for ${startDate} to ${endDate}...`);
    const config = await getReportingConfig(); // A3 2026-09-01: real configurable tax/fee

    // ============================================================================
    // 1. QUERY INVENTORY TRANSACTIONS (type='sale')
    // ============================================================================
    let inventoryTransactions: any[] = [];
    try {
      inventoryTransactions = await inventoryService.getFilteredTransactions({
        type: 'sale',
        startDate: new Date(startDate),
        endDate: new Date(endDate + 'T23:59:59'),
      });
      console.log(`✅ Found ${inventoryTransactions.length} inventory sale transactions`);
    } catch (error) {
      console.warn('⚠️ Could not fetch inventory transactions:', error);
    }

    // ============================================================================
    // 2. GET PRODUCT SALES FROM APPOINTMENTS (productsSold field)
    // ============================================================================
    const appointments = await this.getAppointmentsByDate(startDate, endDate);
    const completedAppointments = appointments.filter((apt) => this.isAppointmentCompleted(apt));

    // Extract productsSold from appointments
    const appointmentProductSales: any[] = [];
    completedAppointments.forEach((apt) => {
      if (apt.productsSold && apt.productsSold.length > 0) {
        apt.productsSold.forEach((product) => {
          appointmentProductSales.push({
            ...product,
            barberId: apt.barberId,
            barberName: apt.barberName,
            date: apt.date,
            appointmentId: apt.id,
          });
        });
      }
    });
    console.log(`✅ Found ${appointmentProductSales.length} products sold via appointments`);

    // ============================================================================
    // 3. GET ALL PRODUCTS FOR DETAILS
    // ============================================================================
    let allProducts: any[] = [];
    try {
      allProducts = await inventoryService.getAllProducts();
    } catch (error) {
      console.warn('⚠️ Could not fetch products:', error);
    }
    const productMap = new Map(allProducts.map((p) => [p.id, p]));

    // ============================================================================
    // 4. AGGREGATE PRODUCT SALES DATA
    // ============================================================================
    const productSalesMap: { [productId: string]: {
      productId: string;
      productName: string;
      category: string;
      unitsSold: number;
      revenue: number;
      cost: number;
      barberSales: { [barberName: string]: { units: number; revenue: number } };
    } } = {};

    // Process inventory transactions
    inventoryTransactions.forEach((txn) => {
      const productId = txn.productId;
      const product = productMap.get(productId);
      const productName = product?.name || txn.productName || 'Unknown Product';
      const category = product?.category || 'Uncategorized';
      const quantity = Math.abs(txn.quantity || 1);
      const revenue = txn.totalPrice || (quantity * (product?.retailPrice || 0));
      const cost = quantity * (product?.wholesalePrice || 0);

      if (!productSalesMap[productId]) {
        productSalesMap[productId] = {
          productId,
          productName,
          category,
          unitsSold: 0,
          revenue: 0,
          cost: 0,
          barberSales: {},
        };
      }

      productSalesMap[productId].unitsSold += quantity;
      productSalesMap[productId].revenue += revenue;
      productSalesMap[productId].cost += cost;

      // Track by barber if available
      const barberName = txn.staffName || 'Unknown';
      if (!productSalesMap[productId].barberSales[barberName]) {
        productSalesMap[productId].barberSales[barberName] = { units: 0, revenue: 0 };
      }
      productSalesMap[productId].barberSales[barberName].units += quantity;
      productSalesMap[productId].barberSales[barberName].revenue += revenue;
    });

    // Process appointment product sales
    appointmentProductSales.forEach((sale) => {
      const productId = sale.productId;
      const productName = sale.productName || 'Unknown Product';
      const product = productMap.get(productId);
      const category = product?.category || 'Uncategorized';
      const quantity = sale.quantity || 1;
      const revenue = sale.total || (quantity * sale.unitPrice);
      const cost = quantity * (product?.wholesalePrice || 0);

      if (!productSalesMap[productId]) {
        productSalesMap[productId] = {
          productId,
          productName,
          category,
          unitsSold: 0,
          revenue: 0,
          cost: 0,
          barberSales: {},
        };
      }

      productSalesMap[productId].unitsSold += quantity;
      productSalesMap[productId].revenue += revenue;
      productSalesMap[productId].cost += cost;

      // Track by barber
      const barberName = sale.barberName || 'Unknown';
      if (!productSalesMap[productId].barberSales[barberName]) {
        productSalesMap[productId].barberSales[barberName] = { units: 0, revenue: 0 };
      }
      productSalesMap[productId].barberSales[barberName].units += quantity;
      productSalesMap[productId].barberSales[barberName].revenue += revenue;
    });

    // Convert to array with top barber info
    const productSales = Object.values(productSalesMap).map((p) => {
      // Find top barber for this product
      let topBarber = 'N/A';
      let maxRevenue = 0;
      Object.entries(p.barberSales).forEach(([barberName, data]) => {
        if (data.revenue > maxRevenue) {
          maxRevenue = data.revenue;
          topBarber = barberName;
        }
      });

      return {
        productId: p.productId,
        productName: p.productName,
        category: p.category,
        unitsSold: p.unitsSold,
        revenue: roundToTwo(p.revenue),
        averagePrice: p.unitsSold > 0 ? roundToTwo(p.revenue / p.unitsSold) : 0,
        cost: roundToTwo(p.cost),
        profit: roundToTwo(p.revenue - p.cost),
        topBarber,
      };
    });

    // ============================================================================
    // 5. CALCULATE TOTALS AND AGGREGATES
    // ============================================================================
    const totalRevenue = productSales.reduce((sum, p) => sum + p.revenue, 0);
    const totalUnits = productSales.reduce((sum, p) => sum + p.unitsSold, 0);
    const totalCost = productSales.reduce((sum, p) => sum + p.cost, 0);
    const averageSale = totalUnits > 0 ? totalRevenue / totalUnits : 0;

    // Top products by revenue
    const topProducts = [...productSales]
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Category breakdown
    const categoryMap: { [key: string]: { revenue: number; units: number; cost: number } } = {};
    productSales.forEach((product) => {
      if (!categoryMap[product.category]) {
        categoryMap[product.category] = { revenue: 0, units: 0, cost: 0 };
      }
      categoryMap[product.category].revenue += product.revenue;
      categoryMap[product.category].units += product.unitsSold;
      categoryMap[product.category].cost += product.cost;
    });

    const categoryBreakdown = Object.entries(categoryMap).map(([category, data]) => ({
      category,
      revenue: roundToTwo(data.revenue),
      units: data.units,
      cost: roundToTwo(data.cost),
      profit: roundToTwo(data.revenue - data.cost),
      percentage: totalRevenue > 0 ? roundToTwo((data.revenue / totalRevenue) * 100) : 0,
    })).sort((a, b) => b.revenue - a.revenue);

    // Barber sales performance (aggregate across all products)
    const barberMap: { [key: string]: { revenue: number; units: number; products: Set<string> } } = {};
    productSales.forEach((product) => {
      Object.entries(product).forEach(([key, value]) => {
        if (key === 'topBarber' && value !== 'N/A') {
          // Find this barber's sales for this product from barberSales in productSalesMap
          const productData = productSalesMap[product.productId];
          if (productData && productData.barberSales) {
            Object.entries(productData.barberSales).forEach(([barberName, barberData]) => {
              if (!barberMap[barberName]) {
                barberMap[barberName] = { revenue: 0, units: 0, products: new Set() };
              }
              barberMap[barberName].revenue += barberData.revenue;
              barberMap[barberName].units += barberData.units;
              barberMap[barberName].products.add(product.productId);
            });
          }
        }
      });
    });

    // Rebuild barber performance from productSalesMap directly
    const barberPerformanceMap: { [key: string]: { revenue: number; units: number; products: Set<string> } } = {};
    Object.values(productSalesMap).forEach((product) => {
      Object.entries(product.barberSales).forEach(([barberName, data]) => {
        if (!barberPerformanceMap[barberName]) {
          barberPerformanceMap[barberName] = { revenue: 0, units: 0, products: new Set() };
        }
        barberPerformanceMap[barberName].revenue += data.revenue;
        barberPerformanceMap[barberName].units += data.units;
        barberPerformanceMap[barberName].products.add(product.productId);
      });
    });

    const barberPerformance = Object.entries(barberPerformanceMap)
      .map(([barberName, data]) => ({
        barberName,
        revenue: roundToTwo(data.revenue),
        units: data.units,
        productsTypes: data.products.size,
        averageSale: data.units > 0 ? roundToTwo(data.revenue / data.units) : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);

    // ============================================================================
    // 6. REAL FINANCIAL FLOW (from appointments)
    // ============================================================================
    // Calculate real discounts and refunds from appointments
    const totalDiscounts = completedAppointments.reduce(
      (sum, apt) => sum + (apt.discountAmount || 0), 0
    );
    const totalRefunds = completedAppointments.reduce(
      (sum, apt) => sum + (apt.refundAmount || 0), 0
    );

    const totalGrossSales = totalRevenue;
    const netSales = totalGrossSales - totalDiscounts - totalRefunds;
    const grossProfit = netSales - totalCost;

    // Taxes & fees from the configurable reporting settings (A3 2026-09-01) — no more hardcoded 8.25%/3%.
    const taxes = netSales * config.productTaxRate;
    const fees = netSales * config.processingFeePct;
    const totalCollected = netSales + taxes - fees;

    // ============================================================================
    // 7. REAL PAYMENT METHOD BREAKDOWN (from appointments)
    // Keys match UI expectations: posCard, squireCard, cash, giftCards
    // ============================================================================
    const paymentBreakdown = {
      cash: 0,
      posCard: 0,      // Card payments via POS terminal
      squireCard: 0,   // Mobile/Squire payments
      giftCards: 0,    // Gift cards and store credit
    };

    // Get payment methods from appointments with products
    completedAppointments.forEach((apt) => {
      if (apt.productsSold && apt.productsSold.length > 0 && apt.paymentMethod) {
        const productTotal = apt.productsSold.reduce((sum, p) => sum + (p.total || 0), 0);
        switch (apt.paymentMethod) {
          case 'cash':
            paymentBreakdown.cash += productTotal;
            break;
          case 'card':
            paymentBreakdown.posCard += productTotal;
            break;
          case 'mobile':
            paymentBreakdown.squireCard += productTotal;
            break;
          case 'store_credit':
          case 'gift_card':
            paymentBreakdown.giftCards += productTotal;
            break;
        }
      }
    });

    // If no payment data, fall back to estimates based on total revenue
    const hasPaymentData = Object.values(paymentBreakdown).some(v => v > 0);
    const paymentMethods = hasPaymentData ? {
      cash: roundToTwo(paymentBreakdown.cash),
      posCard: roundToTwo(paymentBreakdown.posCard),
      squireCard: roundToTwo(paymentBreakdown.squireCard),
      giftCards: roundToTwo(paymentBreakdown.giftCards),
    } : {
      cash: roundToTwo(netSales * 0.20),
      posCard: roundToTwo(netSales * 0.65),
      squireCard: roundToTwo(netSales * 0.10),
      giftCards: roundToTwo(netSales * 0.05),
    };

    console.log(`✅ Product sales report generated: ${productSales.length} products, $${totalRevenue} revenue`);

    return {
      period: {
        startDate,
        endDate,
      },
      summary: {
        totalRevenue: roundToTwo(totalRevenue),
        totalUnits,
        averageSale: roundToTwo(averageSale),
        uniqueProducts: productSales.length,
        totalCost: roundToTwo(totalCost),
        totalProfit: roundToTwo(totalRevenue - totalCost),
        profitMargin: totalRevenue > 0 ? roundToTwo(((totalRevenue - totalCost) / totalRevenue) * 100) : 0,
      },
      // Financial Reconciliation - REAL DATA
      financialFlow: {
        totalGrossSales: roundToTwo(totalGrossSales),
        refunds: roundToTwo(totalRefunds),
        discounts: roundToTwo(totalDiscounts),
        netSales: roundToTwo(netSales),
        costOfInventory: roundToTwo(totalCost),
        grossProfit: roundToTwo(grossProfit),
        grossProfitMargin: totalGrossSales > 0 ? roundToTwo((grossProfit / totalGrossSales) * 100) : 0,
        taxes: roundToTwo(taxes),
        fees: roundToTwo(fees),
        totalCollected: roundToTwo(totalCollected),
      },
      paymentMethods,
      topProducts,
      allProducts: productSales,
      categoryBreakdown,
      barberPerformance,
      trends: {
        bestSellingProduct: topProducts[0]?.productName || 'N/A',
        highestRevenueProduct: topProducts[0]?.productName || 'N/A',
        topCategory: categoryBreakdown[0]?.category || 'N/A',
      },
      // Data source info
      dataSource: {
        inventoryTransactions: inventoryTransactions.length,
        appointmentProductSales: appointmentProductSales.length,
        hasRealPaymentData: hasPaymentData,
      },
    };
  }

  // ============================================================================
  // SERVICE SALES REPORT
  // ============================================================================

  /**
   * Get service sales report data
   */
  async getServiceSalesReport(startDate: string, endDate: string): Promise<any> {
    console.log(`📊 Generating service sales report for ${startDate} to ${endDate}...`);
    const config = await getReportingConfig(); // A3 2026-09-01: real configurable tax/fee

    // Get all appointments for the period
    const appointments = await this.getAppointmentsByDate(startDate, endDate);
    const completedAppointments = appointments.filter((apt) => this.isAppointmentCompleted(apt));

    // Calculate service revenue breakdown
    const serviceMap: { [service: string]: {
      revenue: number;
      count: number;
      avgPrice: number;
      barbers: Set<string>;
    } } = {};

    completedAppointments.forEach((apt) => {
      const service = apt.service || 'Unknown Service';
      if (!serviceMap[service]) {
        serviceMap[service] = { revenue: 0, count: 0, avgPrice: 0, barbers: new Set() };
      }
      serviceMap[service].revenue += apt.price || 0;
      serviceMap[service].count++;
      serviceMap[service].barbers.add(apt.barberName);
    });

    // Calculate averages and convert to array
    const services = Object.entries(serviceMap).map(([serviceName, data]) => ({
      serviceName,
      revenue: roundToTwo(data.revenue),
      bookings: data.count,
      avgPrice: roundToTwo(data.revenue / data.count),
      barbeCount: data.barbers.size,
    })).sort((a, b) => b.revenue - a.revenue);

    const totalRevenue = services.reduce((sum, s) => sum + s.revenue, 0);
    const totalBookings = services.reduce((sum, s) => sum + s.bookings, 0);

    // Most booked services (by count, not revenue)
    const mostBooked = [...services].sort((a, b) => b.bookings - a.bookings).slice(0, 5);

    // Lost Revenue Analysis
    const canceledAppointments = appointments.filter((apt) => apt.status === 'cancelled');
    const noShowAppointments = appointments.filter((apt) => apt.status === 'no-show');

    const lostRevenueCanceled = canceledAppointments.reduce((sum, apt) => sum + (apt.price || 0), 0);
    const lostRevenueNoShows = noShowAppointments.reduce((sum, apt) => sum + (apt.price || 0), 0);
    const totalLostRevenue = lostRevenueCanceled + lostRevenueNoShows;
    const potentialRevenue = totalRevenue + totalLostRevenue;

    // 💰 REAL FINANCIAL RECONCILIATION (from appointments)
    const totalGrossSales = totalRevenue;

    // Real discounts and refunds from appointments
    const discounts = completedAppointments.reduce(
      (sum, apt) => sum + (apt.discountAmount || 0), 0
    );
    const refunds = completedAppointments.reduce(
      (sum, apt) => sum + (apt.refundAmount || 0), 0
    );
    const netSales = totalGrossSales - refunds - discounts;

    // Services typically have no COGS, all revenue is profit
    const costOfServices = 0; // No physical product cost for services
    const grossProfit = netSales - costOfServices;

    // Configurable tax/fee (A3 2026-09-01) — services use serviceTaxRate (NJ haircuts untaxed → 0).
    const taxes = netSales * config.serviceTaxRate;
    const fees = netSales * config.processingFeePct;
    const totalCollected = netSales + taxes - fees;

    // 💰 REAL PAYMENT METHOD BREAKDOWN (from appointments)
    // Keys match UI expectations: posCard, squireCard, cash, giftCards
    const paymentBreakdown = {
      cash: 0,
      posCard: 0,      // Card payments via POS terminal
      squireCard: 0,   // Mobile/Squire payments
      giftCards: 0,    // Gift cards and store credit
    };

    completedAppointments.forEach((apt) => {
      let amount = apt.price || 0;
      // SPLIT TENDER (Jose 2026-10-30): peel the cash portion off before crediting the primary method.
      const splitCash = (apt as any).splitCashAmount || 0;
      if (splitCash > 0) { const c = Math.min(splitCash, amount); paymentBreakdown.cash += c; amount -= c; }
      switch (apt.paymentMethod) {
        case 'cash':
          paymentBreakdown.cash += amount;
          break;
        case 'card':
          paymentBreakdown.posCard += amount;
          break;
        case 'mobile':
          paymentBreakdown.squireCard += amount;
          break;
        case 'store_credit':
        case 'gift_card':
          paymentBreakdown.giftCards += amount;
          break;
      }
    });

    const hasRealPaymentData = Object.values(paymentBreakdown).some(v => v > 0);
    const paymentMethods = hasRealPaymentData ? {
      cash: roundToTwo(paymentBreakdown.cash),
      posCard: roundToTwo(paymentBreakdown.posCard),
      squireCard: roundToTwo(paymentBreakdown.squireCard),
      giftCards: roundToTwo(paymentBreakdown.giftCards),
    } : {
      cash: roundToTwo(netSales * 0.12),
      posCard: roundToTwo(netSales * 0.75),
      squireCard: roundToTwo(netSales * 0.10),
      giftCards: roundToTwo(netSales * 0.03),
    };

    // Service performance with percentages
    const servicesWithPercentage = services.map(service => ({
      ...service,
      percentage: roundToTwo((service.revenue / totalRevenue) * 100),
    }));

    // Barber performance by service revenue
    const barberMap: { [barber: string]: { revenue: number; bookings: number; services: Set<string> } } = {};
    completedAppointments.forEach((apt) => {
      const barber = apt.barberName;
      if (!barberMap[barber]) {
        barberMap[barber] = { revenue: 0, bookings: 0, services: new Set() };
      }
      barberMap[barber].revenue += apt.price || 0;
      barberMap[barber].bookings++;
      barberMap[barber].services.add(apt.service);
    });

    const barberPerformance = Object.entries(barberMap)
      .map(([barberName, data]) => ({
        barberName,
        revenue: roundToTwo(data.revenue),
        bookings: data.bookings,
        servicesOffered: data.services.size,
        avgTicket: roundToTwo(data.revenue / data.bookings),
      }))
      .sort((a, b) => b.revenue - a.revenue);

    return {
      period: {
        startDate,
        endDate,
      },
      summary: {
        totalRevenue: roundToTwo(totalRevenue),
        totalBookings,
        avgServicePrice: roundToTwo(totalRevenue / totalBookings),
        uniqueServices: services.length,
      },
      // Financial Flow
      financialFlow: {
        totalGrossSales: roundToTwo(totalGrossSales),
        refunds: roundToTwo(refunds),
        discounts: roundToTwo(discounts),
        netSales: roundToTwo(netSales),
        costOfServices: roundToTwo(costOfServices),
        grossProfit: roundToTwo(grossProfit),
        grossProfitMargin: totalGrossSales > 0 ? roundToTwo((grossProfit / totalGrossSales) * 100) : 0,
        taxes: roundToTwo(taxes),
        fees: roundToTwo(fees),
        totalCollected: roundToTwo(totalCollected),
      },
      // Lost Revenue Tracking
      lostRevenue: {
        canceled: {
          count: canceledAppointments.length,
          revenue: roundToTwo(lostRevenueCanceled),
        },
        noShows: {
          count: noShowAppointments.length,
          revenue: roundToTwo(lostRevenueNoShows),
        },
        total: roundToTwo(totalLostRevenue),
        potentialRevenue: roundToTwo(potentialRevenue),
        lostPercentage: roundToTwo((totalLostRevenue / potentialRevenue) * 100),
      },
      paymentMethods,
      services: servicesWithPercentage,
      mostBooked,
      barberPerformance,
      trends: {
        topService: services[0]?.serviceName || 'N/A',
        mostBookedService: mostBooked[0]?.serviceName || 'N/A',
        highestAvgPrice: [...services].sort((a, b) => b.avgPrice - a.avgPrice)[0]?.serviceName || 'N/A',
      },
    };
  }

  /**
   * Get Client Analytics Report with LTV, Retention, and CPI Integration
   * EPIC LEADERBOARD DATA! 🔥
   */
  async getClientAnalyticsReport(startDate: string, endDate: string): Promise<any> {
    console.log(`📊 Generating Client Analytics Report: ${startDate} to ${endDate}`);

    try {
      // Get all appointments and clients for the period
      const appointments = await this.getAppointmentsByDate(startDate, endDate);
      const completedAppointments = appointments.filter((apt) => this.isAppointmentCompleted(apt));

      // Get all clients from Firebase
      const clientsRef = collection(db, 'clients');
      const clientsSnapshot = await getDocs(clientsRef);
      const allClients: any[] = [];
      clientsSnapshot.forEach((doc) => {
        allClients.push({ id: doc.id, ...doc.data() });
      });

      // Build client analytics map
      const clientMap: { [clientId: string]: {
        client: any;
        totalRevenue: number;
        visits: number;
        avgSpend: number;
        lastVisit: string;
        firstVisit: string;
        daysSinceLastVisit: number;
        services: string[];
        cpiScore: number;
        retention: number;
        trend: 'up' | 'down' | 'stable';
        riskLevel: 'low' | 'medium' | 'high';
        isVIP: boolean;
      } } = {};

      // Calculate metrics for each client.
      // Attribute by clientId when present; otherwise fall back to the client NAME so imported
      // history (fresha rows carry clientName but no clientId — 75% of appts) still counts toward
      // revenue and per-client rows. Was: hard `if (!apt.clientId) return`, which dropped all
      // imported revenue and showed near-zero. Jose 2026-09-02.
      completedAppointments.forEach((apt) => {
        const nameKey = apt.clientName ? `name:${String(apt.clientName).trim().toLowerCase()}` : null;
        const key = apt.clientId || nameKey;
        if (!key) return;

        if (!clientMap[key]) {
          const foundClient = apt.clientId ? allClients.find((c) => c.id === apt.clientId) : null;

          // Build client with proper name field (Firebase stores firstName/lastName separately)
          const client = foundClient ? {
            ...foundClient,
            name: foundClient.name ||
              `${foundClient.firstName || ''} ${foundClient.lastName || ''}`.trim() ||
              apt.clientName ||
              'Unknown Client',
          } : {
            id: apt.clientId || key,
            name: apt.clientName || 'Unknown Client',
            phone: apt.clientPhone || '',
            email: '',
          };

          clientMap[key] = {
            client,
            totalRevenue: 0,
            visits: 0,
            avgSpend: 0,
            lastVisit: apt.date,
            firstVisit: apt.date,
            daysSinceLastVisit: 0,
            services: [],
            cpiScore: client.cpiScore || 75, // Default CPI if not set
            retention: 0,
            trend: 'stable',
            riskLevel: 'low',
            isVIP: false,
          };
        }

        const clientData = clientMap[key];
        clientData.totalRevenue += apt.price || 0;
        clientData.visits += 1;

        // Track services used
        if (apt.service && !clientData.services.includes(apt.service)) {
          clientData.services.push(apt.service);
        }

        // Update first/last visit
        if (apt.date > clientData.lastVisit) clientData.lastVisit = apt.date;
        if (apt.date < clientData.firstVisit) clientData.firstVisit = apt.date;
      });

      // Calculate derived metrics
      const today = new Date();
      Object.values(clientMap).forEach((data) => {
        data.avgSpend = data.visits > 0 ? data.totalRevenue / data.visits : 0;

        // Days since last visit
        const lastVisitDate = new Date(data.lastVisit);
        data.daysSinceLastVisit = Math.floor((today.getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24));

        // Calculate retention (visits per month)
        const firstVisitDate = new Date(data.firstVisit);
        const monthsActive = Math.max(1, (today.getTime() - firstVisitDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
        data.retention = data.visits / monthsActive;

        // Determine trend based on recent activity
        if (data.daysSinceLastVisit < 14) {
          data.trend = 'up';
        } else if (data.daysSinceLastVisit > 60) {
          data.trend = 'down';
        } else {
          data.trend = 'stable';
        }

        // Determine risk level based on CPI and recent activity
        if (data.cpiScore >= 80 && data.daysSinceLastVisit < 30) {
          data.riskLevel = 'low';
        } else if (data.cpiScore >= 60 && data.daysSinceLastVisit < 60) {
          data.riskLevel = 'medium';
        } else {
          data.riskLevel = 'high';
        }

        // VIP status: High revenue + High CPI
        data.isVIP = data.totalRevenue > 500 && data.cpiScore >= 85;
      });

      // Convert to array and sort
      const clientAnalytics = Object.values(clientMap);

      // 🏆 EPIC LEADERBOARDS! 🏆
      // Top Clients by Lifetime Value
      const topClientsByRevenue = [...clientAnalytics]
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, 20)
        .map((client, index) => ({
          rank: index + 1,
          ...client,
          percentOfTotal: 0, // Will calculate below
        }));

      // Top Clients by CPI Score
      const topClientsByCPI = [...clientAnalytics]
        .sort((a, b) => b.cpiScore - a.cpiScore)
        .slice(0, 20);

      // Most Loyal Clients (by visit frequency)
      const mostLoyalClients = [...clientAnalytics]
        .sort((a, b) => b.retention - a.retention)
        .slice(0, 20);

      // Rising Stars (high growth trend)
      const risingStars = [...clientAnalytics]
        .filter((c) => c.trend === 'up' && c.visits >= 2)
        .sort((a, b) => b.avgSpend - a.avgSpend)
        .slice(0, 10);

      // At-Risk VIP Clients (high value but declining)
      const atRiskVIPs = [...clientAnalytics]
        .filter((c) => c.isVIP && c.riskLevel === 'high')
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, 10);

      // Calculate totals
      const totalRevenue = clientAnalytics.reduce((sum, c) => sum + c.totalRevenue, 0);
      const totalVisits = clientAnalytics.reduce((sum, c) => sum + c.visits, 0);

      // Add percent of total to top clients
      topClientsByRevenue.forEach((client) => {
        client.percentOfTotal = totalRevenue > 0 ? (client.totalRevenue / totalRevenue) * 100 : 0;
      });

      // Summary metrics
      const avgLTV = clientAnalytics.length > 0 ? totalRevenue / clientAnalytics.length : 0;
      const avgCPI = clientAnalytics.length > 0 ? clientAnalytics.reduce((sum, c) => sum + c.cpiScore, 0) / clientAnalytics.length : 0;
      const vipCount = clientAnalytics.filter((c) => c.isVIP).length;
      const atRiskCount = clientAnalytics.filter((c) => c.riskLevel === 'high').length;

      // SHOP-WIDE client truth from the real spine (clientRoster), NOT the ~2%-linked appointments (which made
      // total/LTV/returning off 4-60x — returning read ~1% vs the real 67.3%). Period-scoped counts are kept but
      // relabeled `activeThisPeriod`. BEHAVIOR-PIN: plausible-but-wrong audit (Jose 2026-09-10).
      let shopMetrics: ReturnType<typeof shopClientMetrics> | null = null;
      try { const roster = await loadClientHistory(); if (roster.length) shopMetrics = shopClientMetrics(roster); } catch { /* not imported → fall back to appt-derived */ }

      return {
        period: {
          startDate,
          endDate,
        },
        summary: {
          totalClients: shopMetrics ? shopMetrics.totalClients : clientAnalytics.length,
          totalRevenue,
          avgLTV: shopMetrics ? shopMetrics.avgLtv : avgLTV,
          avgCPI: Math.round(avgCPI),
          vipCount,
          atRiskCount,
          totalVisits,
          avgVisitsPerClient: shopMetrics ? shopMetrics.avgVisits : (clientAnalytics.length > 0 ? totalVisits / clientAnalytics.length : 0),
          returningClients: shopMetrics ? shopMetrics.returning : null,
          returningPct: shopMetrics ? shopMetrics.retentionPct : null,
          activeThisPeriod: clientAnalytics.length, // clients seen in the selected window (period-scoped)
        },
        leaderboards: {
          topByRevenue: topClientsByRevenue,
          topByCPI: topClientsByCPI,
          mostLoyal: mostLoyalClients,
          risingStars,
          atRiskVIPs,
        },
        segments: {
          vip: clientAnalytics.filter((c) => c.isVIP),
          regular: clientAnalytics.filter((c) => !c.isVIP && c.cpiScore >= 60),
          atRisk: clientAnalytics.filter((c) => c.riskLevel === 'high'),
          new: clientAnalytics.filter((c) => c.visits <= 2),
        },
        allClients: clientAnalytics,
      };
    } catch (error) {
      console.error('❌ Error generating client analytics:', error);
      throw error;
    }
  }

  /**
   * Get Barber Performance Report with BPI Integration
   * Returns comprehensive barber analytics with epic leaderboards
   */
  async getBarberPerformanceReport(startDate: string, endDate: string): Promise<any> {
    try {
      console.log(`📊 Generating Barber Performance Report: ${startDate} to ${endDate}`);

      // Get all appointments and staff from Firebase.
      // UNIFIED (2026-08-02): read the populated `barbers` collection, not the retired empty
      // `staff` collection — otherwise this report builds from zero rows.
      const appointments = await this.getAppointmentsByDate(startDate, endDate);
      const staffRef = collection(db, 'barbers');
      const staffSnapshot = await getDocs(staffRef);

      // ── ONE-SOURCE MONEY SPINE (Jose 2026-09-09) ──────────────────────────────────────
      // The leaderboard + charts were disagreeing (e.g. Jose $2,155 on the list vs $2,915 on the
      // bar chart) because this report built money from LIVE appointments while the charts read
      // freshaHistory. Re-source the money (service/retail/tips/tickets) off the SAME reconciled
      // freshaHistory as payroll + the Overview KPIs, so every surface shows ONE number. Growth
      // params (score/tier/bonus/components/nudges) come from each barber's canonical `growth`
      // snapshot. BEHAVIOR-PIN: SLIK-BARBER-PERF-ONE-SOURCE-2026-09-09
      const fhBarbers = await loadFreshaHistory();
      // Imported client history (Fresha insights + sales log) → REAL per-barber retention. Empty until imported.
      let clientRoster: ClientRecord[] = [];
      try { clientRoster = await loadClientHistory(); } catch { /* not imported yet → retention shows "—" */ }
      const fhCur: Record<string, { svc: number; prod: number; tips: number; tickets: number }> = {};
      const fhPriorSvc: Record<string, number> = {};
      {
        const s0 = new Date(startDate + 'T00:00:00');
        const e0 = new Date(endDate + 'T00:00:00');
        const span = Math.max(1, Math.round((e0.getTime() - s0.getTime()) / 86400000) + 1);
        const pe = new Date(s0.getTime() - 86400000);
        const ps = new Date(pe.getTime() - (span - 1) * 86400000);
        const iso = (d: Date) => d.toISOString().slice(0, 10);
        for (const fb of fhBarbers) {
          if (!fb.barberId) continue;
          const c = sumDaysRange(fb, startDate, endDate);
          fhCur[fb.barberId] = { svc: c.svcNet, prod: c.prodNet, tips: c.tips, tickets: c.tickets };
          fhPriorSvc[fb.barberId] = sumDaysRange(fb, iso(ps), iso(pe)).svcNet;
        }
      }

      // REAL period-over-period (2026-09-01): fetch the immediately-prior equal-length window so
      // growthRate/trend come from ACTUAL data, not Math.random(). BEHAVIOR-PIN: SLIK-REPORTING-OVERHAUL-OVERNIGHT-2026-09-01
      const priorRevenueByBarber: { [barberId: string]: number } = {};
      try {
        const s = new Date(startDate + 'T00:00:00');
        const e = new Date(endDate + 'T00:00:00');
        const spanDays = Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
        const priorEnd = new Date(s.getTime() - 86400000);
        const priorStart = new Date(priorEnd.getTime() - (spanDays - 1) * 86400000);
        const iso = (d: Date) => d.toISOString().slice(0, 10);
        const priorAppts = await this.getAppointmentsByDate(iso(priorStart), iso(priorEnd));
        priorAppts.filter((apt) => this.isAppointmentCompleted(apt)).forEach((apt) => {
          if (!apt.barberId) return;
          priorRevenueByBarber[apt.barberId] = (priorRevenueByBarber[apt.barberId] || 0) + (apt.price || 0);
        });
      } catch (e) {
        console.warn('prior-period fetch failed (growth will read 0):', e);
      }
      // per-barber daily revenue, for REAL consistency (coefficient of variation)
      const dailyRevByBarber: { [barberId: string]: { [day: string]: number } } = {};

      // Build comprehensive barber analytics with BPI integration
      const barberMap: {
        [barberId: string]: {
          barber: any;
          totalRevenue: number;
          appointmentsCompleted: number;
          avgTicket: number;
          totalCommission: number;
          servicesPerformed: number;
          productsUpsold: number;
          productRevenue: number;
          serviceRevenue: number;
          hoursWorked: number;
          revenuePerHour: number;
          clientRetentionRate: number;
          uniqueClients: Set<string>;
          repeatClients: number;
          newClients: number;
          bpiScore: number; // BPI INTEGRATION
          trend: 'up' | 'down' | 'stable';
          growthRate: number;
          consistency: number;
          isTopPerformer: boolean;
          needsImprovement: boolean;
        };
      } = {};

      // Initialize barber data
      staffSnapshot.forEach((doc) => {
        const barberData = doc.data();
        // Include every ACTIVE member of the barbers roster. Roles in data are capitalized
        // ("Barber"/"Manager"/"Owner") and here owners/managers also take appointments — the old
        // lowercase `role === 'barber'` check matched nothing → the report showed 0. Jose 2026-09-02.
        const _status = String(barberData.status || 'active').toLowerCase();
        if (_status !== 'inactive' && _status !== 'deleted' && _status !== 'archived') {
          // Build name from firstName/lastName or use existing name field
          const barberName = barberData.name ||
            `${barberData.firstName || ''} ${barberData.lastName || ''}`.trim() ||
            'Unknown Barber';

          barberMap[doc.id] = {
            barber: { id: doc.id, ...barberData, name: barberName },
            totalRevenue: 0,
            appointmentsCompleted: 0,
            avgTicket: 0,
            totalCommission: 0,
            servicesPerformed: 0,
            productsUpsold: 0,
            productRevenue: 0,
            serviceRevenue: 0,
            hoursWorked: 0,
            revenuePerHour: 0,
            clientRetentionRate: 0,
            uniqueClients: new Set(),
            repeatClients: 0,
            newClients: 0,
            bpiScore: barberData.bpiScore || 75, // BPI INTEGRATION - defaults to 75
            trend: 'stable',
            growthRate: 0,
            consistency: 0,
            isTopPerformer: false,
            needsImprovement: false,
          };
        }
      });

      // Process all completed appointments - use helper that supports both status systems
      const completedAppointments = appointments.filter((apt) => this.isAppointmentCompleted(apt));
      const clientVisitHistory: { [clientId: string]: Set<string> } = {};

      completedAppointments.forEach((apt) => {
        const barberId = apt.barberId;
        if (!barberId || !barberMap[barberId]) return;

        const data = barberMap[barberId];

        // Calculate appointment revenue (price is the service price)
        const appointmentRevenue = apt.price || 0;

        data.totalRevenue += appointmentRevenue;
        data.serviceRevenue += appointmentRevenue;
        data.appointmentsCompleted += 1;
        data.servicesPerformed += 1; // Each appointment is one service

        // accumulate per-day revenue for REAL consistency (2026-09-01)
        const dayKey = String(apt.date || '').slice(0, 10);
        if (dayKey) {
          if (!dailyRevByBarber[barberId]) dailyRevByBarber[barberId] = {};
          dailyRevByBarber[barberId][dayKey] = (dailyRevByBarber[barberId][dayKey] || 0) + appointmentRevenue;
        }

        // Track unique clients + client-linkage COVERAGE (Jose 2026-09-10). Retention is only real if
        // appointments are actually linked to clients; most imported Fresha appts are anonymous.
        (data as any)._apptsSeen = ((data as any)._apptsSeen || 0) + 1;
        if (apt.clientId) {
          (data as any)._apptsWithClient = ((data as any)._apptsWithClient || 0) + 1;
          data.uniqueClients.add(apt.clientId);

          if (!clientVisitHistory[apt.clientId]) {
            clientVisitHistory[apt.clientId] = new Set();
          }
          clientVisitHistory[apt.clientId].add(barberId);
        }

        // Calculate hours worked (use duration from appointment, or default to 30 min)
        const durationHours = (apt.duration || 30) / 60;
        data.hoursWorked += durationHours;

        // Calculate commission. FIX (Jose 2026-09-08): barber.commissionRate is stored as a
        // whole-number PERCENT (e.g. 50, 60), but this multiplied revenue by it directly →
        // 100× inflation (the "$610,750 commissions" on a $12k week). Normalize: >1 = percent.
        const rawRate = data.barber.commissionRate ?? 60;
        const commissionRate = rawRate > 1 ? rawRate / 100 : rawRate;
        data.totalCommission += appointmentRevenue * commissionRate;
      });

      // Calculate derived metrics and determine performance status
      const barberAnalytics = Object.values(barberMap).map((data) => {
        const hadLiveAppts = data.appointmentsCompleted > 0;
        data.avgTicket = data.appointmentsCompleted > 0 ? data.totalRevenue / data.appointmentsCompleted : 0;
        data.revenuePerHour = data.hoursWorked > 0 ? data.totalRevenue / data.hoursWorked : 0;

        // Calculate client retention
        const uniqueCount = data.uniqueClients.size;
        if (uniqueCount > 0) {
          let repeatCount = 0;
          data.uniqueClients.forEach((clientId) => {
            const clientVisits = completedAppointments.filter(
              (apt) => apt.clientId === clientId && apt.barberId === data.barber.id
            );
            if (clientVisits.length > 1) repeatCount++;
            if (clientVisits.length === 1) data.newClients++;
          });
          data.repeatClients = repeatCount;
          data.clientRetentionRate = (repeatCount / uniqueCount) * 100;
        }

        // REAL consistency (2026-09-01): 100 − coefficient-of-variation of daily revenue (clamped).
        // <2 active days → 100 (no variance to measure). No more Math.random().
        const dayVals = Object.values(dailyRevByBarber[data.barber.id] || {});
        if (dayVals.length >= 2) {
          const mean = dayVals.reduce((a, b) => a + b, 0) / dayVals.length;
          const variance = dayVals.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / dayVals.length;
          const cv = mean > 0 ? Math.sqrt(variance) / mean : 0;
          data.consistency = Math.max(0, Math.min(100, Math.round((1 - cv) * 100)));
        } else {
          data.consistency = 100;
        }

        // REAL growth rate (2026-09-01): vs the immediately-prior equal-length period. No more Math.random().
        const priorRev = priorRevenueByBarber[data.barber.id] || 0;
        data.growthRate = priorRev > 0 ? Math.round(((data.totalRevenue - priorRev) / priorRev) * 1000) / 10 : 0;

        // Determine trend
        if (data.growthRate > 5) data.trend = 'up';
        else if (data.growthRate < -5) data.trend = 'down';
        else data.trend = 'stable';

        // ── freshaHistory money override + growth surfacing (Jose 2026-09-09) ──────────────
        // Money = the reconciled per-period service/retail/tips (matches payroll + the charts).
        // Growth params come from the barber's canonical `growth` snapshot (current standing).
        const fh = fhCur[data.barber.id];
        const snap: any = (data.barber as any).growth || null;
        let fhTips = 0;
        let retailPct = 0;
        if (fh && (fh.svc > 0 || fh.prod > 0 || fh.tips > 0 || fh.tickets > 0)) {
          data.serviceRevenue = fh.svc;
          data.productRevenue = fh.prod;
          data.totalRevenue = fh.svc;               // leaderboard "revenue" = service (matches chart + payroll)
          fhTips = fh.tips;
          if (fh.tickets > 0) { data.appointmentsCompleted = fh.tickets; data.servicesPerformed = fh.tickets; }
          data.avgTicket = fh.tickets > 0 ? fh.svc / fh.tickets : data.avgTicket;
          retailPct = fh.svc > 0 ? (fh.prod / fh.svc) * 100 : 0;
          const prior = fhPriorSvc[data.barber.id] || 0;
          data.growthRate = prior > 0 ? Math.round(((fh.svc - prior) / prior) * 1000) / 10 : 0;
          data.trend = data.growthRate > 5 ? 'up' : data.growthRate < -5 ? 'down' : 'stable';
        }
        // Retention: period value when we had live appts, else the barber's standing retentionRate.
        // Retention (Jose 2026-09-10) = REAL returning-client rate from the imported client history
        // (Fresha insights + sales log), keyed by barberId over the period. null (→ "—") until imported
        // or when too little linked data. Replaces the appointment-based calc (98% of appts were anonymous).
        const _ret = clientRoster.length
          ? barberRetentionById(clientRoster, data.barber.id, startDate, endDate)
          : { retentionPct: null as number | null };
        const retentionForLeader: number | null = _ret.retentionPct;
        const retentionReliable = _ret.retentionPct != null;
        const clientCoverage = clientRoster.length ? 1 : 0;

        // LIVE growth score (Jose 2026-09-10): recompute from the REAL spine instead of reading the stale
        // `barber.growth` snapshot. Occupancy = last computed standing (real, from recent appts); RETURNING +
        // REBOOKING come from the imported client history (the snapshot's were built on 2%-linked appts →
        // inflated); retail live; rating from the barber. Keeps the Growth Score consistent with its tiles.
        let liveGrowth: any = null;
        if (clientRoster.length && snap && typeof snap.occupancyPct === 'number') {
          const nowMs = Date.now();
          const rebook = barberRebookingById(clientRoster, data.barber.id, nowMs);
          // Tenure from the FIRST real client visit (not the wrong `joinDate`) → correct ramp stage, so a
          // veteran is graded on ALL levers (returning/retail/rebooking), not just occupancy+rating.
          const tenureWeeks = barberTenureWeeksById(clientRoster, data.barber.id, nowMs) ?? 260;
          const stage = Math.max(resolveStageFromTenure(tenureWeeks), snap.stage || 1) as 1 | 2 | 3 | 4;
          const inputs: GrowthInputs = {
            occupancyPct: snap.occupancyPct,
            rebookingPct: rebook != null ? rebook : (snap.components?.rebooking != null ? (snap.components.rebooking / 100) * 50 : 0),
            returningPct: retentionForLeader ?? 0,
            retailPct,
            rating: (data.barber as any).avgRating || 0,
            // avgRating defaults to a PLACEHOLDER 5 → don't let it count as a real 100/100. Rating only
            // scores when there are REAL reviews (reviewCount≥3); until then it drops out (weights renormalize).
            reviewCount: (data.barber as any).reviewCount ?? 0,
            availableHoursPerWeek: snap.availableHoursPerWeek ?? 40,
            tenureWeeks,
            weeklyServiceRevenue: fh && fh.svc ? fh.svc / 4 : (snap.weeklyServiceRevenue || 0),
            stage,
            provisional: snap.provisional,
          };
          try { liveGrowth = computeGrowth(inputs); } catch { liveGrowth = null; }
        }
        const _liveComp = (k: 'occupancy' | 'rebooking' | 'returning' | 'retail' | 'rating'): number => {
          if (!liveGrowth) return 0;
          const c = liveGrowth.components.find((x: any) => x.key === k);
          return c && c.active && c.componentScore >= 0 ? c.componentScore : 0;
        };

        // Top performer criteria: High revenue AND high BPI
        data.isTopPerformer = data.totalRevenue > 3000 && data.bpiScore >= 85;

        // Needs improvement: Low BPI OR low revenue
        data.needsImprovement = data.bpiScore < 70 || (data.totalRevenue < 1000 && data.appointmentsCompleted > 5);

        return {
          ...data.barber,
          ...data,
          uniqueClients: data.uniqueClients.size, // Convert Set to count
          // Money + growth surfacing (Jose 2026-09-09) — one source, canonical growth snapshot.
          tips: Math.round(fhTips * 100) / 100,
          retailPct: Math.round(retailPct * 10) / 10,
          retentionForLeader: retentionForLeader == null ? null : Math.round(retentionForLeader * 10) / 10,
          retentionReliable,
          clientCoverage: Math.round(clientCoverage * 100),
          growthScore: liveGrowth ? liveGrowth.score : (typeof snap?.score === 'number' ? snap.score : null),
          growthTier: liveGrowth ? liveGrowth.tier : (snap?.tier ?? null),
          growthComponents: liveGrowth
            ? { occupancy: _liveComp('occupancy'), rebooking: _liveComp('rebooking'), returning: _liveComp('returning'), retail: _liveComp('retail'), rating: _liveComp('rating') }
            : (snap?.components ?? null),
          growthWeakest: liveGrowth ? (liveGrowth.weakest?.key ?? null) : (snap?.weakest ?? null),
          growthNudges: liveGrowth ? growthNudges(liveGrowth, fh && fh.svc ? fh.svc / 4 : 0) : (Array.isArray(snap?.nudges) ? snap.nudges : []),
          occupancyStanding: typeof snap?.occupancyPct === 'number' ? snap.occupancyPct : null,
          bonusPct: liveGrowth ? liveGrowth.bonus.tierPct : (typeof snap?.bonusPct === 'number' ? snap.bonusPct : 0),
          periodBonus: Math.round(((liveGrowth ? liveGrowth.bonus.tierPct : (typeof snap?.bonusPct === 'number' ? snap.bonusPct : 0)) / 100) * data.serviceRevenue * 100) / 100,
          advancementReason: liveGrowth ? liveGrowth.advancement.reason : (snap?.advancementReason ?? null),
          shouldPromote: liveGrowth ? liveGrowth.advancement.shouldPromote : (snap?.shouldPromote ?? false),
          photo: (data.barber as any).photo || (data.barber as any).photoURL || null,
          colorCode: (data.barber as any).colorCode || null,
        };
      });

      // Filter out barbers with no activity. Include Fresha-era barbers who have reconciled
      // revenue even without live SLIK appointments (Jose 2026-09-09).
      const activeBarbers = barberAnalytics.filter((b) => b.appointmentsCompleted > 0 || b.totalRevenue > 0);

      // Calculate summary metrics
      const summary = {
        totalBarbers: activeBarbers.length,
        totalRevenue: activeBarbers.reduce((sum, b) => sum + b.totalRevenue, 0),
        totalAppointments: activeBarbers.reduce((sum, b) => sum + b.appointmentsCompleted, 0),
        totalCommissions: activeBarbers.reduce((sum, b) => sum + b.totalCommission, 0),
        avgRevenuePerBarber: activeBarbers.length > 0
          ? activeBarbers.reduce((sum, b) => sum + b.totalRevenue, 0) / activeBarbers.length
          : 0,
        avgBPI: activeBarbers.length > 0
          ? activeBarbers.reduce((sum, b) => sum + b.bpiScore, 0) / activeBarbers.length
          : 0,
        topPerformerCount: activeBarbers.filter((b) => b.isTopPerformer).length,
        needsImprovementCount: activeBarbers.filter((b) => b.needsImprovement).length,
      };

      // 🏆 EPIC LEADERBOARDS FOR BARBERS! 🏆
      const leaderboards = {
        // Top 20 barbers by revenue
        topByRevenue: [...activeBarbers]
          .sort((a, b) => b.totalRevenue - a.totalRevenue)
          .slice(0, 20)
          .map((barber, index) => ({
            ...barber,
            rank: index + 1,
            percentOfTotal: (barber.totalRevenue / summary.totalRevenue) * 100,
          })),

        // Top 20 barbers by BPI score
        topByBPI: [...activeBarbers]
          .sort((a, b) => b.bpiScore - a.bpiScore)
          .slice(0, 20)
          .map((barber, index) => ({
            ...barber,
            rank: index + 1,
          })),

        // Top 20 most productive (appointments completed)
        mostProductive: [...activeBarbers]
          .sort((a, b) => b.appointmentsCompleted - a.appointmentsCompleted)
          .slice(0, 20)
          .map((barber, index) => ({
            ...barber,
            rank: index + 1,
          })),

        // Top 10 rising stars (positive growth trend + decent performance)
        risingStars: [...activeBarbers]
          .filter((b) => b.trend === 'up' && b.appointmentsCompleted >= 5)
          .sort((a, b) => b.growthRate - a.growthRate)
          .slice(0, 10)
          .map((barber, index) => ({
            ...barber,
            rank: index + 1,
          })),

        // Top 10 needing improvement (low BPI or underperforming)
        needsImprovement: [...activeBarbers]
          .filter((b) => b.needsImprovement)
          .sort((a, b) => a.bpiScore - b.bpiScore) // Lowest BPI first
          .slice(0, 10)
          .map((barber, index) => ({
            ...barber,
            rank: index + 1,
          })),

        // ── Category leaders (Jose 2026-09-09) — the competitive board, each a different growth lens.
        topByRetail: [...activeBarbers]
          .filter((b) => (b.productRevenue || 0) > 0)
          .sort((a, b) => (b.productRevenue || 0) - (a.productRevenue || 0))
          .slice(0, 20).map((b, i) => ({ ...b, rank: i + 1 })),
        topByRetention: [...activeBarbers]
          .sort((a, b) => ((b as any).retentionForLeader || 0) - ((a as any).retentionForLeader || 0))
          .slice(0, 20).map((b, i) => ({ ...b, rank: i + 1 })),
        topByOccupancy: [...activeBarbers]
          .sort((a, b) => ((b as any).occupancyStanding || 0) - ((a as any).occupancyStanding || 0))
          .slice(0, 20).map((b, i) => ({ ...b, rank: i + 1 })),
        topByAvgTicket: [...activeBarbers]
          .sort((a, b) => (b.avgTicket || 0) - (a.avgTicket || 0))
          .slice(0, 20).map((b, i) => ({ ...b, rank: i + 1 })),
        topByGrowthScore: [...activeBarbers]
          .sort((a, b) => ((b as any).growthScore || 0) - ((a as any).growthScore || 0))
          .slice(0, 20).map((b, i) => ({ ...b, rank: i + 1 })),
        topByGrowthRate: [...activeBarbers]
          .sort((a, b) => (b.growthRate || 0) - (a.growthRate || 0))
          .slice(0, 20).map((b, i) => ({ ...b, rank: i + 1 })),
      };

      // Segment barbers by performance
      const segments = {
        topPerformers: activeBarbers.filter((b) => b.isTopPerformer),
        highPerformers: activeBarbers.filter((b) => b.bpiScore >= 80 && !b.isTopPerformer),
        averagePerformers: activeBarbers.filter((b) => b.bpiScore >= 70 && b.bpiScore < 80),
        needsImprovement: activeBarbers.filter((b) => b.needsImprovement),
        risingStars: activeBarbers.filter((b) => b.trend === 'up' && b.appointmentsCompleted >= 5),
      };

      console.log(`✅ Barber Performance Report generated: ${activeBarbers.length} active barbers`);

      return {
        summary,
        leaderboards,
        segments,
        allBarbers: activeBarbers,
      };
    } catch (error) {
      console.error('❌ Error generating barber performance report:', error);
      throw error;
    }
  }

  /**
   * 📊 BUSINESS INTELLIGENCE DASHBOARD
   * Comprehensive real-time analytics aggregating all business metrics
   */
  async getBusinessIntelligenceDashboard(startDate: string, endDate: string): Promise<any> {
    try {
      console.log(`🧠 Generating BI Dashboard for ${startDate} to ${endDate}`);

      // Calculate previous period for comparison
      const start = new Date(startDate);
      const end = new Date(endDate);
      const periodDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

      const prevStart = new Date(start);
      prevStart.setDate(prevStart.getDate() - periodDays);
      const prevEnd = new Date(start);
      prevEnd.setDate(prevEnd.getDate() - 1);

      const prevStartDate = ymd(prevStart);
      const prevEndDate = ymd(prevEnd);

      // 🔥 PARALLEL DATA FETCH - Get all report data concurrently
      const [
        currentProductReport,
        currentServiceReport,
        currentClientReport,
        currentBarberReport,
        prevProductReport,
        prevServiceReport,
        prevClientReport,
        prevBarberReport,
      ] = await Promise.all([
        this.getProductSalesReport(startDate, endDate),
        this.getServiceSalesReport(startDate, endDate),
        this.getClientAnalyticsReport(startDate, endDate),
        this.getBarberPerformanceReport(startDate, endDate),
        this.getProductSalesReport(prevStartDate, prevEndDate),
        this.getServiceSalesReport(prevStartDate, prevEndDate),
        this.getClientAnalyticsReport(prevStartDate, prevEndDate),
        this.getBarberPerformanceReport(prevStartDate, prevEndDate),
      ]);

      // ── RETENTION & CHURN RADAR (2026-09-12) — REAL retention from the reconciled roster (Fresha
      //    insights), computed AS OF NOW (churn is current-state, not window-bound). The old window-based
      //    avgRetentionRate is ~0 on a 7-day view → it made the weighted health score NaN → rendered 0.
      const _biRoster = await loadClientHistory().catch(() => [] as ClientRecord[]);
      const churnRadar: ChurnRadar = computeChurnRadar(_biRoster, Date.now());
      const clientRetentionMetric = _biRoster.length
        ? churnRadar.retentionPct
        : (currentClientReport.summary.avgRetentionRate || 0);

      // ============================================================================
      // 📈 REAL-TIME KPIs
      // ============================================================================

      const currentRevenue =
        currentProductReport.summary.totalRevenue +
        currentServiceReport.summary.totalRevenue;

      const prevRevenue =
        prevProductReport.summary.totalRevenue +
        prevServiceReport.summary.totalRevenue;

      const revenueGrowth = prevRevenue > 0
        ? ((currentRevenue - prevRevenue) / prevRevenue) * 100
        : 0;

      const currentAppointments = currentBarberReport.summary.totalAppointments;
      const prevAppointments = prevBarberReport.summary.totalAppointments;
      const appointmentGrowth = prevAppointments > 0
        ? ((currentAppointments - prevAppointments) / prevAppointments) * 100
        : 0;

      const currentAvgTicket = currentRevenue / (currentAppointments || 1);
      const prevAvgTicket = prevRevenue / (prevAppointments || 1);
      const avgTicketGrowth = prevAvgTicket > 0
        ? ((currentAvgTicket - prevAvgTicket) / prevAvgTicket) * 100
        : 0;

      const realTimeKPIs = {
        totalRevenue: {
          value: currentRevenue,
          change: revenueGrowth,
          trend: revenueGrowth > 0 ? 'up' : revenueGrowth < 0 ? 'down' : 'stable',
          previousValue: prevRevenue,
        },
        totalAppointments: {
          value: currentAppointments,
          change: appointmentGrowth,
          trend: appointmentGrowth > 0 ? 'up' : appointmentGrowth < 0 ? 'down' : 'stable',
          previousValue: prevAppointments,
        },
        avgTicketSize: {
          value: currentAvgTicket,
          change: avgTicketGrowth,
          trend: avgTicketGrowth > 0 ? 'up' : avgTicketGrowth < 0 ? 'down' : 'stable',
          previousValue: prevAvgTicket,
        },
        productRevenue: {
          value: currentProductReport.summary.totalRevenue,
          change: prevProductReport.summary.totalRevenue > 0
            ? ((currentProductReport.summary.totalRevenue - prevProductReport.summary.totalRevenue) / prevProductReport.summary.totalRevenue) * 100
            : 0,
          trend: currentProductReport.summary.totalRevenue > prevProductReport.summary.totalRevenue ? 'up' : 'down',
        },
        serviceRevenue: {
          value: currentServiceReport.summary.totalRevenue,
          change: prevServiceReport.summary.totalRevenue > 0
            ? ((currentServiceReport.summary.totalRevenue - prevServiceReport.summary.totalRevenue) / prevServiceReport.summary.totalRevenue) * 100
            : 0,
          trend: currentServiceReport.summary.totalRevenue > prevServiceReport.summary.totalRevenue ? 'up' : 'down',
        },
        activeClients: {
          value: currentClientReport.summary.totalClients,
          change: prevClientReport.summary.totalClients > 0
            ? ((currentClientReport.summary.totalClients - prevClientReport.summary.totalClients) / prevClientReport.summary.totalClients) * 100
            : 0,
          trend: currentClientReport.summary.totalClients > prevClientReport.summary.totalClients ? 'up' : 'down',
        },
        avgCPI: {
          value: currentClientReport.summary.avgCPI,
          change: prevClientReport.summary.avgCPI > 0
            ? ((currentClientReport.summary.avgCPI - prevClientReport.summary.avgCPI) / prevClientReport.summary.avgCPI) * 100
            : 0,
          trend: currentClientReport.summary.avgCPI > prevClientReport.summary.avgCPI ? 'up' : 'down',
        },
        avgBPI: {
          value: currentBarberReport.summary.avgBPI,
          change: prevBarberReport.summary.avgBPI > 0
            ? ((currentBarberReport.summary.avgBPI - prevBarberReport.summary.avgBPI) / prevBarberReport.summary.avgBPI) * 100
            : 0,
          trend: currentBarberReport.summary.avgBPI > prevBarberReport.summary.avgBPI ? 'up' : 'down',
        },
      };

      // ============================================================================
      // 🏆 TOP PERFORMERS
      // ============================================================================

      const topPerformers = {
        topBarber: currentBarberReport.leaderboards?.topByRevenue?.[0] || null,
        topService: currentServiceReport.services?.[0] || null, // FIX: services array, not leaderboards
        topProduct: currentProductReport.topProducts?.[0] || null, // FIX: topProducts array, not leaderboards
        topClient: currentClientReport.leaderboards?.topByRevenue?.[0] || null, // FIX: topByRevenue, not topByLifetimeValue
        risingStarBarber: currentBarberReport.leaderboards?.risingStars?.[0] || null,
        risingStarClient: currentClientReport.leaderboards?.risingStars?.[0] || null,
      };

      // ============================================================================
      // ⚠️ AREAS NEEDING ATTENTION
      // ============================================================================

      const needsAttention = {
        lostRevenue: {
          services: currentServiceReport.lostRevenue?.total || 0, // FIX: Use lostRevenue.total from report structure
          products: 0, // Products don't have lost revenue tracking
          total: currentServiceReport.lostRevenue?.total || 0,
        },
        atRiskClients: (currentClientReport.segments?.atRisk || []).length,
        newClients: (currentClientReport.segments?.new || []).length, // FIX: Use 'new' instead of 'inactive'
        underperformingBarbers: (currentBarberReport.leaderboards?.needsImprovement || []).length,
        lowStockProducts: currentProductReport.allProducts?.filter(
          (p: any) => p.stockStatus === 'low' || p.stockStatus === 'out'
        ).length || 0,
        decliningSales: [], // Note: Trend data not available in current report structure
      };

      // ============================================================================
      // 📊 REVENUE BREAKDOWN
      // ============================================================================

      const revenueBreakdown = {
        bySource: {
          services: {
            amount: currentServiceReport.summary.totalRevenue,
            percentage: (currentServiceReport.summary.totalRevenue / currentRevenue) * 100,
            change: prevServiceReport.summary.totalRevenue > 0
              ? ((currentServiceReport.summary.totalRevenue - prevServiceReport.summary.totalRevenue) / prevServiceReport.summary.totalRevenue) * 100
              : 0,
          },
          products: {
            amount: currentProductReport.summary.totalRevenue,
            percentage: (currentProductReport.summary.totalRevenue / currentRevenue) * 100,
            change: prevProductReport.summary.totalRevenue > 0
              ? ((currentProductReport.summary.totalRevenue - prevProductReport.summary.totalRevenue) / prevProductReport.summary.totalRevenue) * 100
              : 0,
          },
        },
        byClientSegment: {
          vip: {
            amount: (currentClientReport.segments?.vip || []).reduce((sum: number, c: any) => sum + c.totalRevenue, 0),
            clients: (currentClientReport.segments?.vip || []).length,
          },
          regular: {
            amount: (currentClientReport.segments?.regular || []).reduce((sum: number, c: any) => sum + c.totalRevenue, 0),
            clients: (currentClientReport.segments?.regular || []).length,
          },
          atRisk: {
            amount: (currentClientReport.segments?.atRisk || []).reduce((sum: number, c: any) => sum + c.totalRevenue, 0),
            clients: (currentClientReport.segments?.atRisk || []).length,
          },
          new: {
            amount: (currentClientReport.segments?.new || []).reduce((sum: number, c: any) => sum + c.totalRevenue, 0),
            clients: (currentClientReport.segments?.new || []).length,
          },
        },
        byBarberPerformance: {
          topPerformers: {
            revenue: (currentBarberReport.segments?.topPerformers || []).reduce((sum: number, b: any) => sum + b.totalRevenue, 0),
            count: (currentBarberReport.segments?.topPerformers || []).length,
          },
          highPerformers: {
            revenue: (currentBarberReport.segments?.highPerformers || []).reduce((sum: number, b: any) => sum + b.totalRevenue, 0),
            count: (currentBarberReport.segments?.highPerformers || []).length,
          },
          averagePerformers: {
            revenue: (currentBarberReport.segments?.averagePerformers || []).reduce((sum: number, b: any) => sum + b.totalRevenue, 0),
            count: (currentBarberReport.segments?.averagePerformers || []).length,
          },
          needsImprovement: {
            revenue: (currentBarberReport.segments?.needsImprovement || []).reduce((sum: number, b: any) => sum + b.totalRevenue, 0),
            count: (currentBarberReport.segments?.needsImprovement || []).length,
          },
        },
      };

      // ============================================================================
      // 🎯 BUSINESS HEALTH SCORE (0-100)
      // ============================================================================

      const healthMetrics = {
        revenueGrowth: Math.min(Math.max(revenueGrowth, -50), 50), // Cap at ±50%
        appointmentGrowth: Math.min(Math.max(appointmentGrowth, -50), 50),
        avgTicketGrowth: Math.min(Math.max(avgTicketGrowth, -50), 50),
        clientRetention: clientRetentionMetric,               // real roster retention (was NaN→0)
        barberPerformance: currentBarberReport.summary.avgBPI || 0,
        clientSatisfaction: currentClientReport.summary.avgCPI || 0,
      };

      // Calculate weighted health score
      const healthScore = Math.round(
        (healthMetrics.revenueGrowth + 50) * 0.20 + // 20% weight (normalized to 0-100)
        (healthMetrics.appointmentGrowth + 50) * 0.15 + // 15% weight
        (healthMetrics.avgTicketGrowth + 50) * 0.10 + // 10% weight
        healthMetrics.clientRetention * 0.20 + // 20% weight
        healthMetrics.barberPerformance * 0.20 + // 20% weight
        healthMetrics.clientSatisfaction * 0.15 // 15% weight
      );

      const healthStatus =
        healthScore >= 85 ? 'excellent' :
        healthScore >= 70 ? 'good' :
        healthScore >= 55 ? 'fair' :
        'needs-improvement';

      // ============================================================================
      // 📈 INSIGHTS & RECOMMENDATIONS
      // ============================================================================

      const insights: string[] = [];

      // Revenue insights
      if (revenueGrowth > 10) {
        insights.push(`🎉 Revenue is up ${revenueGrowth.toFixed(1)}% vs previous period - keep up the great work!`);
      } else if (revenueGrowth < -10) {
        insights.push(`⚠️ Revenue is down ${Math.abs(revenueGrowth).toFixed(1)}% - consider promotional campaigns.`);
      }

      // Appointment insights
      if (appointmentGrowth > 15) {
        insights.push(`📈 Appointments surged by ${appointmentGrowth.toFixed(1)}% - ensure staff capacity.`);
      } else if (appointmentGrowth < -10) {
        insights.push(`📉 Appointments down ${Math.abs(appointmentGrowth).toFixed(1)}% - boost booking campaigns.`);
      }

      // Average ticket insights
      if (avgTicketGrowth > 10) {
        insights.push(`💰 Average ticket increased ${avgTicketGrowth.toFixed(1)}% - upselling is working!`);
      } else if (avgTicketGrowth < -5) {
        insights.push(`💡 Average ticket down ${Math.abs(avgTicketGrowth).toFixed(1)}% - focus on product upsells.`);
      }

      // Client insights
      if (needsAttention.atRiskClients > 0) {
        insights.push(`🔔 ${needsAttention.atRiskClients} clients at risk - send re-engagement campaigns.`);
      }
      if (needsAttention.newClients > 10) {
        insights.push(`👋 ${needsAttention.newClients} new clients - ensure great first impressions!`);
      }

      // Barber insights
      if (needsAttention.underperformingBarbers > 0) {
        insights.push(`👥 ${needsAttention.underperformingBarbers} barbers need coaching - schedule reviews.`);
      }

      // Inventory insights
      if (needsAttention.lowStockProducts > 0) {
        insights.push(`📦 ${needsAttention.lowStockProducts} products low/out of stock - reorder now.`);
      }

      // Lost revenue insights
      if (needsAttention.lostRevenue.total > 1000) {
        insights.push(`💸 $${needsAttention.lostRevenue.total.toFixed(0)} in lost service revenue - reduce no-shows.`);
      }

      console.log(`✅ BI Dashboard generated with ${insights.length} insights`);

      return {
        period: { startDate, endDate, previousStartDate: prevStartDate, previousEndDate: prevEndDate },
        realTimeKPIs,
        topPerformers,
        needsAttention,
        revenueBreakdown,
        healthScore: {
          score: healthScore,
          status: healthStatus,
          metrics: healthMetrics,
        },
        churnRadar,
        insights,
        // Include summary data for quick access
        summaries: {
          products: currentProductReport.summary,
          services: currentServiceReport.summary,
          clients: currentClientReport.summary,
          barbers: currentBarberReport.summary,
        },
      };
    } catch (error) {
      console.error('❌ Error generating BI Dashboard:', error);
      throw error;
    }
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Get appointments by date range from Firebase
   */
  /**
   * Blended revenue for a date range — the SAME reconciled money spine as payroll + the Overview KPIs
   * (one-number-everywhere doctrine). Uses reconciled freshaHistory for every day it covers (per barber),
   * PLUS live completed SLIK appointments for days Fresha does NOT cover for that barber. "Prefer Fresha for
   * its covered dates" so a window spanning the cutover counts both with no double-count. This is what the
   * Register header reads so Today/Week/Month reflect the uploaded Fresha data, not only SLIK-rung checkouts.
   * (Jose 2026-10-17 — "why is today showing $0 i uploaded the info"; mirrors the payroll BLEND 2026-09-10.)
   */
  async getBlendedRevenue(
    startDate: string,
    endDate: string
  ): Promise<{ total: number; service: number; product: number; tips: number; tickets: number }> {
    let service = 0, product = 0, tips = 0, tickets = 0;
    const covered: Record<string, Set<string>> = {}; // barberId -> Fresha-covered dates in window

    // 1) freshaHistory — reconciled per-barber-per-day for the window; track COVERED dates.
    const freshaBarbers = await loadFreshaHistory().catch(() => [] as any[]);
    for (const fb of freshaBarbers) {
      if (!fb.barberId) continue;
      const s = sumDaysRange(fb, startDate, endDate);
      service += s.svcNet; product += s.prodNet; tips += s.tips; tickets += s.tickets;
      const set = (covered[fb.barberId] ||= new Set<string>());
      for (const iso of Object.keys(fb.days || {})) { if (iso >= startDate && iso <= endDate) set.add(iso); }
    }

    // 2) BLEND — add live completed SLIK appts on days Fresha does NOT already cover for that barber.
    const appointments = await this.getAppointmentsByDate(startDate, endDate);
    for (const apt of appointments.filter((a) => this.isAppointmentCompleted(a))) {
      const bid = apt.barberId;
      if (!bid || covered[bid]?.has(apt.date)) continue; // skip unassigned + Fresha-covered days
      service += apt.price || 0;
      product += apt.productsSold?.reduce((p: number, x: any) => p + (x.total || 0), 0) || 0;
      tips += (apt as any).tipAmount || 0;
      tickets += 1;
    }

    return {
      total: roundToTwo(service + product + tips),
      service: roundToTwo(service),
      product: roundToTwo(product),
      tips: roundToTwo(tips),
      tickets,
    };
  }

  private async getAppointmentsByDate(startDate: string, endDate: string): Promise<Appointment[]> {
    try {
      // Try to get from Firebase first
      const appointmentsRef = collection(db, 'appointments');
      await ensureActiveShopId();
      const q = scopedQuery(
        appointmentsRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const appointments: Appointment[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        appointments.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          checkedInAt: data.checkedInAt?.toDate(),
          startedAt: data.startedAt?.toDate(),
          completedAt: data.completedAt?.toDate(),
        } as Appointment);
      });

      console.log(`✅ Loaded ${appointments.length} appointments from Firebase`);
      return appointments;
    } catch (error) {
      console.warn('⚠️ Firebase query failed, falling back to appointmentService:', error);

      // Fallback to appointmentService (in-memory)
      const allAppointments = appointmentService.getAllAppointments();
      return allAppointments.filter((apt) => apt.date >= startDate && apt.date <= endDate);
    }
  }

  /**
   * Get clients from appointments
   */
  /**
   * REAL new-vs-returning for a window, from the client roster by FIRST-VISIT date (not appointments.clientId
   * ~2% linked, nor the lifetime `totalVisits` seed). new = first-ever visit is on/after the window start;
   * returning = seen before it. Anonymous walk-ins are excluded. BEHAVIOR-PIN: plausible-but-wrong audit.
   */
  private async classifyWindowClients(appointments: Appointment[], windowStartISO: string): Promise<{ newClients: number; returningClients: number; firstTimeReturns: number; distinct: number }> {
    const roster = await loadClientHistory().catch(() => [] as ClientRecord[]);
    const byName = new Map<string, ClientRecord>();
    for (const c of roster) byName.set((c.name || '').trim().toLowerCase().replace(/\s+/g, ' '), c);
    const WALK = new Set(['', 'walk-in', 'walk in', 'walkin']);
    const seen = new Set<string>();
    let newC = 0, returningC = 0, firstReturn = 0;
    for (const a of appointments) {
      const nm = ((a as any).clientName || '').trim().toLowerCase().replace(/\s+/g, ' ');
      if (WALK.has(nm) || seen.has(nm)) continue;
      seen.add(nm);
      const rec = byName.get(nm);
      if (!rec || !rec.firstVisit) { newC++; continue; }
      if (rec.firstVisit >= windowStartISO) newC++;
      else { returningC++; if ((rec.totalVisits || 0) === 2) firstReturn++; }
    }
    return { newClients: newC, returningClients: returningC, firstTimeReturns: firstReturn, distinct: seen.size };
  }

  /**
   * Calculate daily staff performance
   */
  private async calculateDailyStaffPerformance(
    appointments: Appointment[]
  ): Promise<BarberDailyPerformance[]> {
    const barberIds = [...new Set(appointments.map((apt) => apt.barberId))];
    const performance: BarberDailyPerformance[] = [];
    // Shop sliding-scale config once for the loop (per-barber flat mode resolved below). Jose 2026-09-01.
    const commissionPay = mergeCommissionPay(await shopSettingsService.getSettingsByType('commissionPay'));
    // MONEY-CRITICAL (Jose 2026-10-28): await the private pay vault so flat barbers aren't read as sliding.
    await barberDataService.ensurePrivatePayLoaded();

    for (const barberId of barberIds) {
      const barberAppointments = appointments.filter((apt) => apt.barberId === barberId);
      const barberName = barberAppointments[0]?.barberName || 'Unknown';

      const revenue = barberAppointments.reduce((sum, apt) => sum + (apt.price || 0), 0);

      // 💰 REAL PRODUCT SALES: Sum from productsSold
      const productSales = barberAppointments.reduce((sum, apt) => {
        if (apt.productsSold && apt.productsSold.length > 0) {
          return sum + apt.productsSold.reduce((pSum, p) => pSum + (p.total || 0), 0);
        }
        return sum;
      }, 0);

      // 💰 REAL TIPS: Sum tipAmount from appointments
      const tips = barberAppointments.reduce((sum, apt) => sum + (apt.tipAmount || 0), 0);

      // Per-barber pay mode (flat vs shop sliding scale) — same resolver payroll uses. Jose 2026-09-01.
      const _bRec = barberDataService.getBarberById(barberId);
      const _svc = commissionForBarber(_bRec, revenue, commissionPay.tiers);
      const _prodComm = roundToTwo(productSales * (shopSettingsService.getProductCommissionRate() / 100));
      const totalCommission = roundToTwo(_svc.commission + _prodComm);

      const totalEarnings = calculateBarberPay({
        serviceCommission: totalCommission,
        productCommission: 0,
        tips,
      });

      performance.push({
        barberId,
        barberName,
        appointments: barberAppointments.length,
        revenue: roundToTwo(revenue),
        productSales: roundToTwo(productSales),
        tips: roundToTwo(tips),
        averageTicket: roundToTwo(calculateAverageTicket(revenue, barberAppointments.length)),
        commission: roundToTwo(totalCommission),
        totalEarnings: roundToTwo(totalEarnings),
        noShows: 0, // TODO: Calculate actual no-shows when appointment lifecycle tracking is added
      });
    }

    // Sort by revenue (highest first)
    performance.sort((a, b) => b.revenue - a.revenue);

    return performance;
  }

  /**
   * Find busiest hour from appointments
   */
  private findBusiestHour(appointments: Appointment[]): {
    hour: number | null;
    appointments: number;
    revenue: number;
  } {
    // hour is a NUMBER 0-23 (the display does 12h formatting + hour+1). Returns hour=null when there
    // are no appointments, so the report shows "no data" instead of a bogus 12 AM window (Jose 2026-08-30).
    const hourData: { [hour: number]: { count: number; revenue: number } } = {};

    appointments.forEach((apt) => {
      // MIXED-FORMAT FIX (Jose 2026-10-30): appt.time is stored as both "13:00" and "1:00 PM" in prod;
      // the old `split(':')[0]` read "1:00 PM" as hour 1 (AM), bucketing every PM visit into the morning
      // → a wrong "busiest hour". Use the AM/PM-tolerant shared parser.
      const mins = parseTimeToMinutes(apt.time);
      if (mins == null) return;
      const hour = Math.floor(mins / 60);
      if (!hourData[hour]) {
        hourData[hour] = { count: 0, revenue: 0 };
      }
      hourData[hour].count++;
      hourData[hour].revenue += apt.price || 0;
    });

    // Find hour with most appointments
    let busiestHour: number | null = null;
    let maxCount = 0;
    let maxRevenue = 0;

    Object.entries(hourData).forEach(([hour, data]) => {
      if (data.count > maxCount) {
        busiestHour = parseInt(hour, 10);
        maxCount = data.count;
        maxRevenue = data.revenue;
      }
    });

    return {
      hour: busiestHour,
      appointments: maxCount,
      revenue: roundToTwo(maxRevenue),
    };
  }

  /**
   * Get daily breakdown for a date range
   */
  private async getDailyBreakdown(
    startDate: string,
    endDate: string,
    appointments: Appointment[]
  ): Promise<{ date: string; revenue: number; appointments: number }[]> {
    const breakdown: { [date: string]: { revenue: number; appointments: number } } = {};

    // Initialize all dates in range
    let currentDate = new Date(startDate);
    const end = new Date(endDate);

    while (currentDate <= end) {
      const dateStr = ymd(currentDate);
      breakdown[dateStr] = { revenue: 0, appointments: 0 };
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Fill in appointment data
    appointments.forEach((apt) => {
      if (breakdown[apt.date]) {
        breakdown[apt.date].appointments++;
        breakdown[apt.date].revenue += apt.price || 0;
      }
    });

    // Convert to array and round revenue
    return Object.entries(breakdown).map(([date, data]) => ({
      date,
      revenue: roundToTwo(data.revenue),
      appointments: data.appointments,
    }));
  }

  /**
   * Get top performers for a period
   */
  private async getTopPerformers(
    appointments: Appointment[]
  ): Promise<{ barberName: string; revenue: number; appointments: number }[]> {
    const performance = await this.calculateDailyStaffPerformance(appointments);

    return performance.slice(0, 3).map((p) => ({
      barberName: p.barberName,
      revenue: p.revenue,
      appointments: p.appointments,
    }));
  }

  /**
   * Calculate service breakdown
   */
  private calculateServiceBreakdown(
    appointments: Appointment[]
  ): { serviceName: string; count: number; revenue: number }[] {
    const services: { [service: string]: { count: number; revenue: number } } = {};

    appointments.forEach((apt) => {
      const service = apt.service || 'Unknown';
      if (!services[service]) {
        services[service] = { count: 0, revenue: 0 };
      }
      services[service].count++;
      services[service].revenue += apt.price || 0;
    });

    return Object.entries(services)
      .map(([serviceName, data]) => ({
        serviceName,
        count: data.count,
        revenue: roundToTwo(data.revenue),
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }

  /**
   * Get previous week date range
   */
  private getPreviousWeekRange(startDate: string): { startDate: string; endDate: string } {
    const start = new Date(startDate);
    const prevStart = new Date(start);
    prevStart.setDate(start.getDate() - 7);

    const prevEnd = new Date(prevStart);
    prevEnd.setDate(prevStart.getDate() + 6);

    return {
      startDate: ymd(prevStart),
      endDate: ymd(prevEnd),
    };
  }

  /**
   * Get previous period date range (same length as current period)
   */
  private getPreviousPeriodRange(startDate: string, endDate: string): { startDate: string; endDate: string } {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysInPeriod = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    const prevEnd = new Date(start);
    prevEnd.setDate(start.getDate() - 1);

    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevEnd.getDate() - daysInPeriod);

    return {
      startDate: ymd(prevStart),
      endDate: ymd(prevEnd),
    };
  }

  /**
   * Get previous month date range
   */
  private getPreviousMonthRange(year: number, month: number): { startDate: string; endDate: string } {
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;

    const startDate = ymd(new Date(prevYear, prevMonth - 1, 1));
    const endDate = ymd(new Date(prevYear, prevMonth, 0));

    return { startDate, endDate };
  }

  /**
   * Get weekly breakdown for a month
   */
  private async getWeeklyBreakdownForMonth(
    startDate: string,
    endDate: string,
    appointments: Appointment[]
  ): Promise<{ weekNumber: number; startDate: string; endDate: string; revenue: number; appointments: number }[]> {
    const weeks: { weekNumber: number; startDate: string; endDate: string; revenue: number; appointments: number }[] = [];

    let weekStart = new Date(startDate);
    let weekNumber = 1;

    while (weekStart <= new Date(endDate)) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      // Don't exceed month end
      const actualWeekEnd = weekEnd > new Date(endDate) ? new Date(endDate) : weekEnd;

      const weekStartStr = ymd(weekStart);
      const weekEndStr = ymd(actualWeekEnd);

      // Get appointments for this week
      const weekAppointments = appointments.filter(
        (apt) => apt.date >= weekStartStr && apt.date <= weekEndStr
      );

      const weekRevenue = weekAppointments.reduce((sum, apt) => sum + (apt.price || 0), 0);

      weeks.push({
        weekNumber,
        startDate: weekStartStr,
        endDate: weekEndStr,
        revenue: roundToTwo(weekRevenue),
        appointments: weekAppointments.length,
      });

      // Move to next week
      weekStart.setDate(weekStart.getDate() + 7);
      weekNumber++;
    }

    return weeks;
  }
}

// Export singleton instance
export const reportingDataService = new ReportingDataService();

// ============================================================================
// COMPARISON ENGINE (C1) — pure, reusable period + delta helpers.
// No Firestore, no side effects. C2 (Growth/YoY tile) and C3 (Finance Summary
// compare column) both consume these. Additive only — nothing above changes.
// ============================================================================

export type ComparisonGrain = 'week' | 'month' | 'year';
export type ComparisonDirection = 'up' | 'down' | 'flat';

/** A calendar range as inclusive local `YYYY-MM-DD` strings. */
export interface PeriodRange {
  start: string;
  end: string;
}

/** The current period plus its immediately-prior comparable period. */
export interface ComparisonPeriods {
  grain: ComparisonGrain;
  current: PeriodRange;
  prior: PeriodRange;
}

/** A single metric compared against its prior period. */
export interface MetricComparison {
  value: number;
  prior: number;
  deltaAbs: number;
  deltaPct: number;
  direction: ComparisonDirection;
}

/** Normalize a Date | 'YYYY-MM-DD' into a LOCAL midnight Date (no UTC drift). */
function toLocalDate(anchor: Date | string): Date {
  if (typeof anchor === 'string') return parseYmd(anchor);
  return new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
}

/** Last day-of-month for a given year + 0-based month. */
function lastDayOfMonth(year: number, month0: number): number {
  return new Date(year, month0 + 1, 0).getDate();
}

/**
 * The current calendar unit containing `anchor`:
 *  - week  → Sunday–Saturday week containing the anchor
 *  - month → 1st–last day of the anchor's month
 *  - year  → Jan 1–Dec 31 of the anchor's year
 */
export function getPeriodRange(anchor: Date | string, grain: ComparisonGrain): PeriodRange {
  const d = toLocalDate(anchor);
  const y = d.getFullYear();
  const m = d.getMonth();

  if (grain === 'week') {
    const dow = d.getDay(); // 0 = Sunday
    const start = new Date(y, m, d.getDate() - dow);
    const end = new Date(y, m, d.getDate() - dow + 6);
    return { start: ymd(start), end: ymd(end) };
  }

  if (grain === 'month') {
    const start = new Date(y, m, 1);
    const end = new Date(y, m, lastDayOfMonth(y, m));
    return { start: ymd(start), end: ymd(end) };
  }

  // year
  return { start: ymd(new Date(y, 0, 1)), end: ymd(new Date(y, 11, 31)) };
}

/**
 * Shift an arbitrary [start,end] range back by one grain-unit, giving the
 * immediately-prior comparable window:
 *  - week  → same range shifted back 7 days (span preserved)
 *  - month → the full previous calendar month
 *  - year  → same month/day range one year back (Feb 29 clamps to Feb 28)
 * Works on both calendar-unit ranges (from getPeriodRange) and custom ranges
 * (e.g. a year-to-date window → same YTD window one year prior).
 */
export function shiftRangeBack(range: PeriodRange, grain: ComparisonGrain): PeriodRange {
  const s = parseYmd(range.start);
  const e = parseYmd(range.end);

  if (grain === 'week') {
    const start = new Date(s.getFullYear(), s.getMonth(), s.getDate() - 7);
    const end = new Date(e.getFullYear(), e.getMonth(), e.getDate() - 7);
    return { start: ymd(start), end: ymd(end) };
  }

  if (grain === 'month') {
    // Previous calendar month, derived from the range start (JS normalizes the
    // Dec→Jan year rollover). Full-month semantics: 1st → last day.
    const ps = new Date(s.getFullYear(), s.getMonth() - 1, 1);
    const pe = new Date(ps.getFullYear(), ps.getMonth() + 1, 0);
    return { start: ymd(ps), end: ymd(pe) };
  }

  // year — same month/day, one year back, clamping the day into that month.
  const shiftYear = (dt: Date): Date => {
    const py = dt.getFullYear() - 1;
    const pm = dt.getMonth();
    const day = Math.min(dt.getDate(), lastDayOfMonth(py, pm));
    return new Date(py, pm, day);
  };
  return { start: ymd(shiftYear(s)), end: ymd(shiftYear(e)) };
}

/**
 * The prior comparable period for the calendar unit containing `anchor`.
 * Convenience wrapper: getPeriodRange → shiftRangeBack.
 */
export function computePriorPeriod(anchor: Date | string, grain: ComparisonGrain): PeriodRange {
  return shiftRangeBack(getPeriodRange(anchor, grain), grain);
}

/** Both the current and prior periods for the unit containing `anchor`. */
export function getComparisonPeriods(anchor: Date | string, grain: ComparisonGrain): ComparisonPeriods {
  const current = getPeriodRange(anchor, grain);
  const prior = shiftRangeBack(current, grain);
  return { grain, current, prior };
}

/**
 * Compare a current metric value against its prior-period value.
 * deltaPct reuses calculateGrowth (handles prior === 0). direction is 'flat'
 * only when the two values are exactly equal.
 */
export function buildComparison(current: number, prior: number): MetricComparison {
  const value = roundToTwo(current);
  const priorRounded = roundToTwo(prior);
  const deltaAbs = roundToTwo(current - prior);
  const deltaPct = roundToTwo(calculateGrowth(current, prior));
  let direction: ComparisonDirection = 'flat';
  if (current > prior) direction = 'up';
  else if (current < prior) direction = 'down';
  return { value, prior: priorRounded, deltaAbs, deltaPct, direction };
}
